import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  BadgePercent,
  Banknote,
  BellRing,
  BookOpen,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Clock3,
  FileCheck2,
  FileText,
  Handshake,
  Headset,
  History,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Megaphone,
  Mail,
  MessageSquareText,
  Newspaper,
  Plug,
  Receipt,
  RotateCcw,
  Search,
  Settings2,
  ShieldAlert,
  Tags,
  Trash2,
  UserCog,
  UsersRound,
  Wallet,
  Download,
  Images,
  ListTodo,
  Bug,
  ScrollText,
  Sparkles,
  CalendarHeart,
  FileVideo,
} from "lucide-react";
import Nav from "../components/Nav";
import api, { fmtINRFull, fmtMoneyNumber, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import useHighlightRow from "../lib/useHighlightRow";
import {
  AdminMaster, AdminBoost, AdminTemplates, AdminFAQs,
  AdminCMS, AdminBroadcast, AdminSettings, AdminAudit, AdminReports,
  AdminReviewsModeration, AdminProviders, AdminBlogs,
} from "./admin/AdminEnterprise";
import AdminConcierge from "./admin/AdminConcierge";
import AdminOutstationReport from "./admin/AdminOutstationReport";
import AdminQuestionEditor from "./admin/AdminQuestionEditor";
import AdminSubscriptions from "./admin/AdminSubscriptions";
import AdminCategoryRequests from "./admin/AdminCategoryRequests";
import AdminCityRequests from "./admin/AdminCityRequests";
import AdminArtistKycFollowups from "./admin/AdminArtistKycFollowups";
import AdminOnboardingManagers from "./admin/AdminOnboardingManagers";
import AdminActionCenter from "./admin/AdminActionCenter";
import AdminDetailDrawer, { DrawerFacts, DrawerSection } from "./admin/AdminDetailDrawer";
import AdminAdmins from "./admin/AdminAdmins";
import AdminPaymentGateway from "./admin/AdminPaymentGateway";
import AdminPaymentReconciliation from "./admin/AdminPaymentReconciliation";
import AdminPlatformSettings from "./admin/AdminPlatformSettings";
import AdminHomepageHero from "./admin/AdminHomepageHero";
import AdminCustomerStories from "./admin/AdminCustomerStories";
import AdminHomepageSpotlight from "./admin/AdminHomepageSpotlight";
import AdminEventTypeArtists from "./admin/AdminEventTypeArtists";
import AdminKycUpload from "./admin/AdminKycUpload";
import AdminEventMedia from "./admin/AdminEventMedia";
import { AdminActivityLog, AdminErrorLog, UserActivityDrawer } from "./admin/AdminLogs";
import { getVirtualWindow } from "./admin/virtualWindow";
import AdminListPagination from "./admin/AdminListPagination";
import VirtualizedList from "./admin/VirtualizedList";
import { PayoutConsole, AtRiskDashboard } from "../components/PaymentPayoutWidgets";
import { summarizeBookingPayment } from "./bookingDetailSummary";
import {
  AdminPayoutRetryQueue, AdminReportSchedules, AdminManagerScorecard,
  AdminWhatsAppTemplates, AdminReportSnapshots,
} from "./admin/AdminIter88";
import { formatPaymentDueLabel } from "../lib/paymentTimeline";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

// Loaded on demand: it pulls in the chart library, which other pages don't need.
const CustomerJourneyAnalytics = React.lazy(() => import("./admin/CustomerJourneyAnalytics"));

// Iter 57 — Sidebar → required permission. If the current admin lacks the
// permission, the item is hidden from the sidebar entirely. `null` means
// "any admin can see this" (overview is always shown as an empty state).
const SIDEBAR_GROUPS = [
  {
    title: "DASHBOARD",
    items: [
      { id: "action-center", label: " Action Center", perm: null },
      { id: "overview", label: " Overview", perm: null },
      { id: "manager-scorecard", label: " Manager Scorecard", perm: "analytics.view" },
      { id: "analytics", label: " KPIs & Customer Journey", perm: "analytics.view" },
      { id: "at-risk", label: " At-Risk Bookings", perm: "bookings.view" },
    ],
  },
  {
    title: "MARKETPLACE",
    items: [
      { id: "bookings", label: " Bookings", perm: "bookings.view" },
      { id: "event-media", label: " Event Media", perm: "bookings.view" },
    ],
  },
  {
    title: "USERS & ARTISTS",
    items: [
      { id: "artists", label: " Artists", perm: "artists.moderate" },
      { id: "kyc", label: " KYC Queue", perm: "artists.moderate" },
      { id: "kyc-upload", label: " Upload Artist KYC", perm: "artists.moderate" },
      { id: "artist-kyc-followups", label: " Artist KYC Follow-ups", perm: "artists.moderate" },
      { id: "onboarding-managers", label: " Onboarding Managers", perm: "artists.moderate" },
      { id: "agreements", label: " Artist Agreements", perm: "artists.moderate" },
      { id: "commercial-deals", label: " Commercial Deals", perm: "payments.refund" },
      { id: "users", label: " Users", perm: "users.view" },
      { id: "reviews-mod", label: " Reviews Moderation", perm: "artists.moderate" },
      { id: "disputes", label: " Disputes", perm: "bookings.override" },
      { id: "concierge", label: " Concierge", perm: "bookings.view" },
      { id: "category-requests", label: " Category Requests", perm: "artists.moderate" },
      { id: "city-requests", label: " City Requests", perm: "artists.moderate" },
    ],
  },
  {
    title: "PAYMENTS & PAYOUTS",
    items: [
      { id: "payment-recon", label: " Payment Reconciliation", perm: "payments.view" },
      { id: "payout-console", label: " Payout Console", perm: "payments.view" },
      { id: "bulk-payouts", label: " Bulk Payouts", perm: "payments.view" },
      { id: "payout-retry", label: " Payout Retry Queue", perm: "payments.view" },
      { id: "refunds", label: " Refunds", perm: "payments.refund" },
      { id: "refund-audit", label: " Refund Auditor", perm: "payments.refund" },
      { id: "subscriptions", label: " Subscriptions", perm: "subscriptions.manage" },
      { id: "coupons", label: " Coupons", perm: "cms.manage" },
    ],
  },
  {
    title: "GROWTH",
    items: [
      { id: "boost", label: " Boost Manager", perm: "artists.moderate" },
      { id: "broadcast", label: " Broadcast", perm: "notifications.send" },
    ],
  },
  {
    title: "COMMUNICATION & CONTENT",
    items: [
      { id: "templates", label: " Templates", perm: "cms.manage" },
      { id: "wa-templates", label: " WhatsApp Templates", perm: "settings.manage" },
      { id: "faqs", label: " FAQs", perm: "cms.manage" },
      { id: "cms", label: " CMS Pages", perm: "cms.manage" },
      { id: "blogs", label: " Blogs", perm: "cms.manage" },
      { id: "homepage-hero", label: " Homepage Hero Videos", perm: "cms.manage" },
      { id: "homepage-spotlight", label: " Homepage Spotlight", perm: "cms.manage" },
      { id: "customer-stories", label: " Customer Stories Slider", perm: "cms.manage" },
      { id: "event-type-artists", label: " Artist Event Types", perm: "cms.manage" },
      { id: "settings", label: " Site Notices", perm: "settings.manage" },
    ],
  },
  {
    title: "REPORTS",
    items: [
      { id: "reports", label: " Reports", perm: "analytics.view" },
      { id: "outstation-report", label: " Outstation Report", perm: "analytics.view" },
      { id: "report-snapshots", label: " Snapshot History", perm: "analytics.view" },
      { id: "report-schedules", label: " Report Schedules", perm: "analytics.view" },
    ],
  },
  {
    title: "PLATFORM & CONFIGURATION",
    items: [
      { id: "providers", label: " Providers", perm: "settings.manage" },
      { id: "master", label: " Master Data", perm: "settings.manage" },
      { id: "questionnaire", label: " Questionnaire", perm: "cms.manage" },
      { id: "platform-settings", label: " Platform Settings", perm: "settings.manage" },
    ],
  },
  {
    title: "ADMINISTRATION",
    items: [
      { id: "admins", label: " Admin Team", perm: "admins.manage" },
      { id: "audit", label: " Audit Logs", perm: "admins.manage" },
      { id: "activity-log", label: " Activity Log", perm: "admins.manage" },
      { id: "error-log", label: " Error Log", perm: "admins.manage" },
    ],
  },
];

const SIDEBAR_ICONS = {
  "action-center": ListTodo,
  overview: LayoutDashboard,
  "manager-scorecard": ClipboardCheck,
  analytics: ChartNoAxesCombined,
  "at-risk": Activity,
  bookings: CalendarDays,
  artists: UsersRound,
  kyc: FileCheck2,
  "artist-kyc-followups": Mail,
  "onboarding-managers": UsersRound,
  agreements: FileText,
  "commercial-deals": Handshake,
  users: UsersRound,
  "reviews-mod": MessageSquareText,
  disputes: ShieldAlert,
  concierge: Headset,
  "category-requests": Tags,
  "city-requests": MapPin,
  "payment-recon": Receipt,
  "payout-console": Wallet,
  "bulk-payouts": Banknote,
  "payout-retry": RotateCcw,
  refunds: RotateCcw,
  "refund-audit": ClipboardList,
  subscriptions: Clock3,
  coupons: BadgePercent,
  boost: Activity,
  broadcast: Megaphone,
  templates: FileText,
  "wa-templates": MessageSquareText,
  faqs: BookOpen,
  cms: Newspaper,
  blogs: Newspaper,
  "homepage-hero": Building2,
  "homepage-spotlight": Sparkles,
  "customer-stories": MessageSquareText,
  "event-type-artists": CalendarHeart,
  "kyc-upload": FileVideo,
  "event-media": Images,
  settings: BellRing,
  reports: ChartNoAxesCombined,
  "outstation-report": MapPin,
  "report-snapshots": History,
  "report-schedules": Clock3,
  providers: Plug,
  master: Building2,
  questionnaire: ListChecks,
  "platform-settings": Settings2,
  admins: UserCog,
  audit: CheckCircle2,
  "activity-log": ScrollText,
  "error-log": Bug,
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    // Iter 63.5 — read ?tab= from URL so notification click-through lands on the right tab.
    const p = new URLSearchParams(window.location.search).get("tab");
    return p || "action-center";
  });

  useEffect(() => {
    const p = new URLSearchParams(location.search).get("tab");
    if (p) setTab(p);
    else if (tab !== "action-center") setTab("action-center");
  }, [location.search]);
  const [stats, setStats] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState(() =>
    Object.fromEntries(SIDEBAR_GROUPS.map((group) => [group.title, false]))
  );
  // Iter 57 — Read the caller's RBAC permissions so we can HIDE sidebar
  // modules they lack the permission for. Backend still enforces access —
  // this is a UX polish so admins don't stare at empty error states for
  // sections they can't touch. `null` while loading = show nothing to be safe.
  const [myPerms, setMyPerms] = useState(null);

  const toggleGroup = (title) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    // Stats endpoint requires analytics.view — silently fall back to {} if 403.
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/rbac/me")
      .then((r) => setMyPerms(new Set(r.data.admin_permissions || [])))
      .catch(() => setMyPerms(new Set()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || user.role !== "admin") return null;

  const visibleSidebarGroups = SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((x) => !x.perm || (myPerms && myPerms.has(x.perm))),
  })).filter((group) => group.items.length > 0);
  const canSee = (perm) => !perm || (myPerms && myPerms.has(perm));

  // If the currently-selected tab isn't visible anymore (e.g. permissions
  // narrowed since login), fall back to Overview so we don't render a
  // hidden section's content.
  // Customer Journey was merged into Analytics; old ?tab=customer-journey
  // links open Analytics on its visitor reports.
  const resolvedTab = tab === "customer-journey" ? "analytics" : tab;
  const effectiveTab = visibleSidebarGroups.flatMap((group) => group.items).some((x) => x.id === resolvedTab) ? resolvedTab : "action-center";

  return (
    <>
      <Nav />
      <div className="dash-wrap admin-dashboard-layout" data-testid="admin-dashboard">
      <aside className="sidebar">
        {visibleSidebarGroups.map((group) => (
          <React.Fragment key={group.title}>
            <button
              type="button"
              className="sb-section"
              style={{
                marginTop: "var(--space-3)",
                fontSize: 10,
                letterSpacing: "0.12em",
                opacity: 0.75,
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "inherit",
                padding: "0 0 var(--space-1) 0",
              }}
              onClick={() => toggleGroup(group.title)}
              aria-expanded={!collapsedGroups[group.title]}
            >
              <span>{group.title}</span>
              <span>{collapsedGroups[group.title] ? "▸" : "▾"}</span>
            </button>
            {!collapsedGroups[group.title] && group.items.map((x) => {
              const Icon = SIDEBAR_ICONS[x.id];
              return (
                <div key={x.id} className={`sb-item ${effectiveTab === x.id ? "active" : ""}`} onClick={() => setTab(x.id)} data-testid={`sb-${x.id}`}>
                  <Icon className="admin-sidebar-icon" aria-hidden="true" />
                  <span>{x.label.trim()}</span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </aside>
      <main className="dash-content">
        <div style={{ marginTop: "var(--space-4)" }}>
          {effectiveTab === "overview" && (
            <>
              <div className="dash-head">
                <div><h1>Platform Overview</h1><p>All systems operational</p></div>
              </div>

              <div className="kpi-grid">
                <Kpi icon={<ChartNoAxesCombined size={18} aria-hidden="true" />} cls="kpi-icon-gold" num={fmtINRFull(stats.gmv || 0)} label="Marketplace GMV (artist fees)" onClick={() => setTab("analytics")} />
                <Kpi icon={<CalendarDays size={18} aria-hidden="true" />} cls="kpi-icon-purple" num={stats.total_bookings || 0} label="Bookings" onClick={() => setTab("bookings")} />
                <Kpi icon={<UsersRound size={18} aria-hidden="true" />} cls="kpi-icon-green" num={stats.total_users || 0} label="Users" onClick={() => setTab("users")} />
                <Kpi icon={<Banknote size={18} aria-hidden="true" />} cls="kpi-icon-blue" num={fmtINRFull(stats.bookTalent_total_collected || 0)} label="BookTalent Total Collected" onClick={() => setTab("payment-recon")} />
              </div>

              <div className="kpi-grid mb-24">
                <Kpi icon={<Receipt size={18} aria-hidden="true" />} cls="kpi-icon-amber" num={fmtINRFull(stats.gst_collected || 0)} label="GST Collected (18%)" onClick={() => setTab("payment-recon")} />
                <Kpi icon={<BadgePercent size={18} aria-hidden="true" />} cls="kpi-icon-purple" num={fmtINRFull(stats.subscription_revenue || 0)} label="Subscription Revenue" onClick={() => setTab("subscriptions")} />
                <Kpi icon={<Activity size={18} aria-hidden="true" />} cls="kpi-icon-blue" num={fmtINRFull(stats.boost_revenue || 0)} label="Boost Revenue" onClick={() => setTab("boost")} />
              </div>

              <div className="kpi-grid mb-24">
                <Kpi icon={<RotateCcw size={18} aria-hidden="true" />} cls="kpi-icon-amber" num={stats.pending_refunds || 0} label="Refunds Pending" onClick={() => setTab("refunds")} />
                <Kpi icon={<FileCheck2 size={18} aria-hidden="true" />} cls="kpi-icon-blue" num={stats.pending_kyc || 0} label="KYC Pending" onClick={() => setTab("kyc")} />
                <Kpi icon={<ShieldAlert size={18} aria-hidden="true" />} cls="kpi-icon-red" num={stats.open_disputes || 0} label="Open Disputes" onClick={() => setTab("disputes")} />
              </div>

              {/* Feb-2026 requirement KPIs — funnel & operational health */}
              <div className="kpi-grid mb-24" data-testid="admin-kpis-req">
                <Kpi icon={<MessageSquareText size={18} aria-hidden="true" />} cls="kpi-icon-purple" num={stats.new_leads || 0} label="New Leads" onClick={() => setTab("analytics")} />
                <Kpi icon={<CheckCircle2 size={18} aria-hidden="true" />} cls="kpi-icon-green" num={stats.active_bookings || 0} label="Active Bookings" onClick={() => setTab("bookings")} />
                <Kpi icon={<CalendarDays size={18} aria-hidden="true" />} cls="kpi-icon-blue" num={stats.upcoming_events || 0} label="Upcoming Events" onClick={() => setTab("bookings")} />
                <Kpi icon={<FileText size={18} aria-hidden="true" />} cls="kpi-icon-amber" num={stats.agreements_pending || 0} label="Agreements Pending" onClick={() => setTab("kyc")} />
              </div>
              <div className="kpi-grid mb-24">
                <Kpi icon={<Wallet size={18} aria-hidden="true" />} cls="kpi-icon-amber" num={stats.customer_payment_pending || 0} label="Customer Payments Pending" onClick={() => setTab("bookings")} />
                <Kpi icon={<Clock3 size={18} aria-hidden="true" />} cls="kpi-icon-red" num={stats.overdue_payments || 0} label="Overdue Payments" onClick={() => setTab("bookings")} />
                <Kpi icon={<Banknote size={18} aria-hidden="true" />} cls="kpi-icon-purple" num={stats.artist_payout_pending || 0} label="Artist Payouts Pending" onClick={() => setTab("payout-console")} />
                <Kpi icon={<Wallet size={18} aria-hidden="true" />} cls="kpi-icon-gold" num={fmtINRFull(stats.remaining_amount || 0)} label="Remaining ₹ Across Bookings" onClick={() => setTab("bookings")} />
              </div>
              <div className="kpi-grid mb-24">
                <Kpi icon={<Building2 size={18} aria-hidden="true" />} cls="kpi-icon-purple" num={stats.agency_bookings || 0} label="Agency Bookings" onClick={() => setTab("bookings")} />
              </div>
            </>
          )}

          {effectiveTab === "overview" && <OverviewAdmin stats={stats} />}
          {effectiveTab === "action-center" && <AdminActionCenter toast={toast} onOpenTab={setTab} />}
          {effectiveTab === "artists" && <AdminArtists toast={toast} />}
          {effectiveTab === "bookings" && <AdminBookings />}
          {effectiveTab === "concierge" && <AdminConcierge toast={toast} />}
          {effectiveTab === "outstation-report" && <AdminOutstationReport toast={toast} />}
          {effectiveTab === "kyc" && (
            <AdminKYC
              toast={toast}
              onNavigateToRequest={(requestTab, requestId) => {
                nav(requestId
                  ? `/admin?tab=${requestTab}&highlight=${encodeURIComponent(requestId)}`
                  : `/admin?tab=${requestTab}`);
              }}
            />
          )}
          {effectiveTab === "artist-kyc-followups" && <AdminArtistKycFollowups toast={toast} />}
          {effectiveTab === "onboarding-managers" && <AdminOnboardingManagers toast={toast} />}
          {effectiveTab === "agreements" && <AdminAgreements />}
          {effectiveTab === "category-requests" && <AdminCategoryRequests toast={toast} />}
          {effectiveTab === "city-requests" && <AdminCityRequests toast={toast} />}
          {effectiveTab === "refunds" && <AdminRefunds toast={toast} />}
          {effectiveTab === "refund-audit" && <AdminRefundAuditor toast={toast} />}
          {effectiveTab === "bulk-payouts" && <AdminBulkPayouts toast={toast} />}
          {effectiveTab === "commercial-deals" && <AdminCommercialDeals toast={toast} />}
          {effectiveTab === "coupons" && <AdminCoupons toast={toast} />}
          {effectiveTab === "subscriptions" && <AdminSubscriptions toast={toast} />}
          {effectiveTab === "admins" && <AdminAdmins toast={toast} />}
          {effectiveTab === "users" && <AdminUsers toast={toast} />}
          {effectiveTab === "disputes" && <AdminDisputes toast={toast} />}
          {effectiveTab === "master" && <AdminMaster toast={toast} />}
          {effectiveTab === "questionnaire" && <AdminQuestionEditor />}
          {effectiveTab === "boost" && <AdminBoost toast={toast} />}
          {effectiveTab === "templates" && <AdminTemplates toast={toast} />}
          {effectiveTab === "faqs" && <AdminFAQs toast={toast} />}
          {effectiveTab === "cms" && <AdminCMS toast={toast} />}
          {effectiveTab === "blogs" && <AdminBlogs toast={toast} />}
          {effectiveTab === "homepage-hero" && <AdminHomepageHero />}
          {effectiveTab === "homepage-spotlight" && <AdminHomepageSpotlight />}
          {effectiveTab === "customer-stories" && <AdminCustomerStories />}
          {effectiveTab === "event-type-artists" && <AdminEventTypeArtists />}
          {effectiveTab === "kyc-upload" && <AdminKycUpload />}
          {effectiveTab === "event-media" && <AdminEventMedia />}
          {effectiveTab === "broadcast" && <AdminBroadcast toast={toast} />}
          {effectiveTab === "reports" && <AdminReports />}
          {effectiveTab === "reviews-mod" && <AdminReviewsModeration toast={toast} />}
          {effectiveTab === "providers" && <AdminProviders toast={toast} />}
          {effectiveTab === "payment-gateway" && <AdminPaymentGateway toast={toast} />}
          {effectiveTab === "payment-recon" && <AdminPaymentReconciliation toast={toast} />}
          {effectiveTab === "platform-settings" && <AdminPlatformSettings />}
          {effectiveTab === "at-risk" && <AtRiskDashboard />}
          {effectiveTab === "analytics" && (
            <React.Suspense fallback={<div className="card card-pad text-muted">Loading analytics…</div>}>
              <CustomerJourneyAnalytics initialTab={tab === "customer-journey" ? "overview" : "kpis"} />
            </React.Suspense>
          )}
          {effectiveTab === "payout-console" && <PayoutConsole />}
          {effectiveTab === "payout-retry" && <AdminPayoutRetryQueue />}
          {effectiveTab === "manager-scorecard" && <AdminManagerScorecard />}
          {effectiveTab === "report-schedules" && <AdminReportSchedules />}
          {effectiveTab === "report-snapshots" && <AdminReportSnapshots />}
          {effectiveTab === "wa-templates" && <AdminWhatsAppTemplates />}
          {effectiveTab === "settings" && <AdminSettings toast={toast} />}
          {effectiveTab === "audit" && <AdminAudit />}
          {effectiveTab === "activity-log" && <AdminActivityLog />}
          {effectiveTab === "error-log" && <AdminErrorLog toast={toast} />}
        </div>
      </main>
      </div>
    </>
  );
}

const Kpi = ({ icon, cls, num, label, onClick }) => (
  <div
    className="kpi"
    data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
    onClick={onClick}
    onKeyDown={(event) => {
      if (onClick && (event.key === "Enter" || event.key === " ")) onClick();
    }}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    style={onClick ? { cursor: "pointer" } : undefined}
    title={onClick ? "Open related details" : undefined}
  >
    <div className="kpi-top"><div className={`kpi-icon ${cls}`}>{icon}</div></div>
    <div className="kpi-num">{num}</div>
    <div className="kpi-label">{label}</div>
  </div>
);

function OverviewAdmin({ stats }) {
  return (
    <div className="card card-pad" data-testid="admin-overview">
      <h3 className="font-serif fs-20 fw-700 mb-16">Quick Stats</h3>
      <div className="grid grid-3">
        <div className="card card-pad" data-testid="stat-total-artists"><div className="flex items-center gap-8 mb-8"><UsersRound size={16} aria-hidden="true" /><div className="text-muted fs-11">Total Artists</div></div><div className="fs-20 fw-700">{stats.total_artists ?? 0}</div></div>
        <div className="card card-pad" data-testid="stat-total-customers"><div className="flex items-center gap-8 mb-8"><UsersRound size={16} aria-hidden="true" /><div className="text-muted fs-11">Total Customers</div></div><div className="fs-20 fw-700">{stats.total_customers ?? 0}</div></div>
        <div className="card card-pad" data-testid="stat-bookings-today"><div className="flex items-center gap-8 mb-8"><CalendarDays size={16} aria-hidden="true" /><div className="text-muted fs-11">Bookings Today</div></div><div className="fs-20 fw-700">{stats.bookings_today ?? 0}</div></div>
        <div className="card card-pad" data-testid="stat-pending-bookings"><div className="flex items-center gap-8 mb-8"><Clock3 size={16} aria-hidden="true" /><div className="text-muted fs-11">Pending Bookings</div></div><div className="fs-20 fw-700">{stats.pending_bookings ?? 0}</div></div>
      </div>
    </div>
  );
}

const ARTIST_KYC_LABELS = {
  live: ["Live", "pill-green"],
  kyc_under_review: ["KYC pending review", "pill-amber"],
  kyc_changes_required: ["KYC changes required", "pill-amber"],
  kyc_rejected: ["KYC rejected", "pill-red"],
  kyc_approved: ["KYC approved · T&C pending", "pill-blue"],
  tnc_pending: ["T&C pending", "pill-blue"],
  agreement_generated: ["Agreement pending", "pill-blue"],
  kyc_pending: ["KYC not submitted", "pill-amber"],
  registration_pending: ["KYC not submitted", "pill-amber"],
  unverified: ["KYC not submitted", "pill-amber"],
  approved: ["KYC approved", "pill-blue"],
  pending: ["KYC pending", "pill-amber"],
  suspended: ["Suspended", "pill-red"],
};
const artistKycInfo = (a) => {
  const status = a.user?.suspended || a.suspended ? "suspended" : a.go_live_progress?.kyc_status || a.kyc_status || "kyc_pending";
  return ARTIST_KYC_LABELS[status] || [status.replace(/_/g, " "), "pill-amber"];
};
const artistReminderMailto = (a) => `mailto:${encodeURIComponent(a.user.email)}?subject=${encodeURIComponent("Complete your BookTalent artist profile")}&body=${encodeURIComponent(`Hi ${a.stage_name || "there"},\n\nYour BookTalent artist profile is ${a.profile_completion.percent}% complete. Please log in and complete these sections:\n\n${(a.profile_completion.missing || []).map((item) => `- ${item}`).join("\n")}\n\nThank you,\nBookTalent Team`)}`;

function AdminArtists({ toast }) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [artistType, setArtistType] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [kycStatus, setKycStatus] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [boostedOnly, setBoostedOnly] = useState(false);
  const [suspendedOnly, setSuspendedOnly] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const tableScrollRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const getFilterParams = () => ({
    ...(searchQuery ? { search: searchQuery } : {}),
    ...(artistType ? { artist_type: artistType } : {}),
    ...(category.trim() ? { category: category.trim() } : {}),
    ...(city.trim() ? { city: city.trim() } : {}),
    ...(kycStatus ? { kyc_status: kycStatus } : {}),
    ...(featuredOnly ? { featured: true } : {}),
    ...(boostedOnly ? { boosted: true } : {}),
    ...(suspendedOnly ? { suspended: true } : {}),
  });
  const reload = async (targetPage = page) => {
    setLoading(true);
    try {
      const response = await api.get("/admin/artists", {
        params: { page: targetPage, limit: pageSize, ...getFilterParams() },
      });
      const payload = response.data || {};
      const items = Array.isArray(payload.items) ? payload.items : Array.isArray(payload) ? payload : [];
      const nextTotal = Number(payload.total ?? items.length);
      const nextPages = Number(payload.pages) || Math.max(1, Math.ceil(nextTotal / pageSize));
      setList(items);
      setTotal(nextTotal);
      setPages(nextPages);
      if (targetPage > nextPages) setPage(nextPages);
    } catch (error) {
      toast(formatApiError(error), "error");
      setList([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchQuery(search.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [search]);
  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    setScrollTop(0);
    reload(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchQuery, artistType, category, city, kycStatus, featuredOnly, boostedOnly, suspendedOnly]);
  const exportArtists = async () => {
    setExporting(true);
    try {
      const response = await api.get("/admin/artists/export", {
        params: getFilterParams(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `artists-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast("Artist export downloaded", "success");
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setExporting(false);
    }
  };
  const clearFilters = () => {
    setSearch("");
    setSearchQuery("");
    setArtistType("");
    setCategory("");
    setCity("");
    setKycStatus("");
    setFeaturedOnly(false);
    setBoostedOnly(false);
    setSuspendedOnly(false);
    setPage(1);
  };
  const hasFilters = Boolean(
    search || searchQuery || artistType || category || city || kycStatus || featuredOnly || boostedOnly || suspendedOnly
  );
  const feature = async (uid) => { await api.post(`/admin/artists/${uid}/feature`); toast("Feature toggled"); reload(page); };
  const removeBoost = async (artist) => {
    if (!window.confirm(`Remove the legacy Boosted flag from ${artist.stage_name || "this artist"}? This does not cancel a boost subscription.`)) return;
    try {
      await api.post(`/admin/artists/${artist.user_id}/unboost`);
      toast("Boosted flag removed", "success");
      reload(page);
    } catch (e) {
      toast(formatApiError(e), "error");
    }
  };
  const suspend = async (uid) => {
    const r = await api.post(`/admin/artists/${uid}/suspend`);
    toast(r.data.suspended ? "Suspended" : "Unsuspended");
    reload(page);
  };
  const virtualRows = getVirtualWindow(scrollTop, 64, 576, list.length);
  const visibleArtists = list.slice(virtualRows.start, virtualRows.end);
  // Looked up from the list so the drawer reflects changes after each reload.
  const selected = list.find((a) => a.user_id === selectedId) || null;
  const moreFilterCount = [artistType, category.trim(), city.trim(), featuredOnly, boostedOnly, suspendedOnly].filter(Boolean).length;
  // Dialogs open above the page, so close the drawer first.
  const openDialog = (setter, value) => { setSelectedId(null); setter(value); };
  return (
    <div className="card" data-testid="admin-artists">
      <div className="card-head">
        <div className="card-title">Artists ({total.toLocaleString("en-IN")})</div>
        <div className="flex items-center gap-8" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={exportArtists}
            disabled={exporting || loading}
            data-testid="admin-artists-export"
          >
            <Download size={14} aria-hidden="true" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <label className="admin-page-size">
            <span>Rows per page</span>
            <select
              className="field-input"
              value={pageSize}
              onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}
              data-testid="admin-artists-page-size"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
        </div>
      </div>
      <div
        className="admin-artists-filters"
        data-testid="admin-artists-filters"
        style={{ display: "flex", alignItems: "end", flexWrap: "wrap", gap: "var(--space-2)", padding: "0 18px 12px" }}
      >
        <label className="admin-artists-search">
          <span className="field-label">Search artists</span>
          <span className="admin-artists-search-input">
            <Search size={15} aria-hidden="true" />
            <input
              className="field-input"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, email, city, category"
              aria-label="Search artists by name, email, city, or category"
              data-testid="admin-artists-search"
            />
          </span>
        </label>
        <label className="admin-artists-filter">
          <span className="field-label">KYC status</span>
          <select className="field-input" value={kycStatus} onChange={(event) => { setKycStatus(event.target.value); setPage(1); }} data-testid="admin-artists-kyc-filter">
            <option value="">All KYC statuses</option>
            <option value="kyc_pending">Not submitted</option>
            <option value="registration_pending">Registration pending</option>
            <option value="unverified">Unverified</option>
            <option value="kyc_under_review">Pending review</option>
            <option value="kyc_changes_required">Changes required</option>
            <option value="kyc_rejected">Rejected</option>
            <option value="approved">Approved</option>
            <option value="kyc_approved">KYC approved</option>
            <option value="tnc_pending">T&amp;C pending</option>
            <option value="agreement_generated">Agreement pending</option>
            <option value="live">Live</option>
          </select>
        </label>
        <button
          type="button"
          className={`btn btn-sm ${showMoreFilters || moreFilterCount ? "btn-gold" : "btn-ghost"}`}
          onClick={() => setShowMoreFilters((open) => !open)}
          aria-expanded={showMoreFilters}
          data-testid="admin-artists-more-filters"
        >
          More filters{moreFilterCount ? ` (${moreFilterCount})` : ""}
        </button>
        {hasFilters && <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters} data-testid="admin-artists-clear-filters">Clear filters</button>}
      </div>
      {showMoreFilters && (
        <div style={{ display: "flex", alignItems: "end", flexWrap: "wrap", gap: "var(--space-2)", padding: "0 18px 14px" }} data-testid="admin-artists-more-filter-panel">
          <label className="admin-artists-filter">
            <span className="field-label">Artist type</span>
            <select className="field-input" value={artistType} onChange={(event) => { setArtistType(event.target.value); setPage(1); }} data-testid="admin-artists-type-filter">
              <option value="">All types</option>
              <option value="normal">Normal artist</option>
              <option value="service">BookTalent service artist</option>
            </select>
          </label>
          <label className="admin-artists-filter">
            <span className="field-label">Category</span>
            <input className="field-input" value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }} placeholder="Any category" data-testid="admin-artists-category-filter" />
          </label>
          <label className="admin-artists-filter">
            <span className="field-label">City</span>
            <input className="field-input" value={city} onChange={(event) => { setCity(event.target.value); setPage(1); }} placeholder="Any city" data-testid="admin-artists-city-filter" />
          </label>
          <label className="admin-artists-checkbox">
            <input type="checkbox" checked={featuredOnly} onChange={(event) => { setFeaturedOnly(event.target.checked); setPage(1); }} data-testid="admin-artists-featured-filter" />
            Featured
          </label>
          <label className="admin-artists-checkbox">
            <input type="checkbox" checked={boostedOnly} onChange={(event) => { setBoostedOnly(event.target.checked); setPage(1); }} data-testid="admin-artists-boosted-filter" />
            Boosted
          </label>
          <label className="admin-artists-checkbox">
            <input type="checkbox" checked={suspendedOnly} onChange={(event) => { setSuspendedOnly(event.target.checked); setPage(1); }} data-testid="admin-artists-suspended-filter" />
            Suspended
          </label>
        </div>
      )}
      <div
        className="table-wrap admin-virtual-table-scroll admin-artists-virtual-scroll"
        ref={tableScrollRef}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        aria-busy={loading}
      >
        <table className="table admin-virtual-table admin-artists-virtual-table">
          <thead><tr><th>Artist</th><th>Category</th><th>Readiness</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="empty">{loading ? "Loading artists…" : "No artists found."}</td></tr>
            )}
            {virtualRows.topPadding > 0 && (
              <tr className="admin-virtual-spacer" aria-hidden="true">
                <td colSpan={5} style={{ height: virtualRows.topPadding, "--virtual-spacer-height": `${virtualRows.topPadding}px` }} />
              </tr>
            )}
            {visibleArtists.map((a) => {
              const completion = a.profile_completion || { percent: 0, missing: [] };
              const percent = Math.max(0, Math.min(100, Number(completion.percent) || 0));
              const goLive = a.go_live_progress || { percent: 0, completed_steps: 0, total_steps: 8 };
              const goLivePercent = Math.max(0, Math.min(100, Number(goLive.percent) || 0));
              const [kycLabel, kycClass] = artistKycInfo(a);
              return (
              <tr
                key={a.id}
                data-testid={`artist-row-${a.user_id}`}
                className={`admin-compact-row ${selectedId === a.user_id ? "is-selected" : ""}`}
                tabIndex={0}
                onClick={() => setSelectedId(a.user_id)}
                onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); setSelectedId(a.user_id); } }}
              >
                <td>
                  <div className="admin-cell-main">{a.stage_name}</div>
                  <div className="admin-cell-sub">{a.user?.deleted ? (a.user.deactivated_email || a.user.email) : a.user?.email}</div>
                </td>
                <td>
                  <div className="admin-cell-main fw-400">{a.category || "—"}</div>
                  <div className="admin-cell-sub">{[a.city, a.is_service_artist ? "Service artist" : "Normal artist", `${a.events_done || 0} events`].filter(Boolean).join(" · ")}</div>
                </td>
                <td data-testid={`artist-progress-${a.user_id}`}>
                  <div className="flex gap-12" style={{ alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }} data-testid={`profile-completion-${a.user_id}`} title={(completion.missing || []).length ? `Missing: ${completion.missing.join(", ")}` : "Profile complete"}>
                      <div className="admin-cell-sub">Profile {percent}%</div>
                      <div className="admin-mini-bar"><span style={{ width: `${percent}%`, background: percent >= 80 ? "var(--green)" : percent >= 45 ? "var(--amber)" : "var(--red)" }} /></div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }} data-testid={`go-live-completion-${a.user_id}`} title={`${goLive.completed_steps} of ${goLive.total_steps} go-live milestones complete`}>
                      <div className="admin-cell-sub">Go-live {goLive.completed_steps}/{goLive.total_steps}</div>
                      <div className="admin-mini-bar"><span style={{ width: `${goLivePercent}%`, background: goLive.is_live ? "var(--green)" : "var(--purple-mid)" }} /></div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`pill ${kycClass}`} data-testid={`artist-kyc-status-${a.user_id}`}>{kycLabel}</span>
                  {(a.is_featured || a.is_boosted) && (
                    <div className="admin-cell-sub" style={{ marginTop: 4 }}>{[a.is_featured && "Featured", a.is_boosted && "Boosted"].filter(Boolean).join(" · ")}</div>
                  )}
                </td>
                <td onClick={(event) => event.stopPropagation()}>
                  <div className="flex gap-8">
                    <Link className="btn btn-gold btn-xs" to={`/staff/artists/${a.user_id}`} data-testid={`review-${a.user_id}`} title="Full profile, questionnaire answers and what is missing">Review</Link>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setSelectedId(a.user_id)} data-testid={`manage-${a.user_id}`}>Manage</button>
                  </div>
                </td>
              </tr>
              );
            })}
            {virtualRows.bottomPadding > 0 && (
              <tr className="admin-virtual-spacer" aria-hidden="true">
                <td colSpan={5} style={{ height: virtualRows.bottomPadding, "--virtual-spacer-height": `${virtualRows.bottomPadding}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="admin-list-pagination">
        <span className="text-muted fs-12" data-testid="admin-artists-page-summary">
          {total ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString("en-IN")}` : "No artists"}
        </span>
        {pages > 1 && (
          <Pagination className="cd-pagination">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page <= 1}
                  onClick={(event) => { event.preventDefault(); if (page > 1) setPage(page - 1); }}
                  data-testid="admin-artists-prev"
                />
              </PaginationItem>
              <PaginationItem><span className="cd-page-indicator" aria-current="page">Page {page} of {pages}</span></PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page >= pages}
                  onClick={(event) => { event.preventDefault(); if (page < pages) setPage(page + 1); }}
                  data-testid="admin-artists-next"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
      <AdminDetailDrawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        testId="admin-artist-drawer"
        title={selected?.stage_name}
        subtitle={selected ? [selected.category, selected.city].filter(Boolean).join(" · ") : ""}
        badge={selected && (
          <>
            <span className={`pill ${artistKycInfo(selected)[1]}`}>{artistKycInfo(selected)[0]}</span>
            {selected.is_featured && <span className="pill pill-gold">Featured</span>}
            {selected.is_boosted && <span className="pill pill-gold">Boosted</span>}
          </>
        )}
        footer={selected && (
          <Link to={`/staff/artists/${selected.user_id}`} className="btn btn-gold btn-sm">Review full profile</Link>
        )}
      >
        {selected && (() => {
          const completion = selected.profile_completion || { percent: 0, missing: [] };
          const goLive = selected.go_live_progress || { percent: 0, completed_steps: 0, total_steps: 8 };
          return (
            <>
              <DrawerSection title="Details">
                <DrawerFacts items={[
                  ["Email", selected.user?.deleted ? (selected.user.deactivated_email || selected.user.email) : selected.user?.email],
                  ["Type", selected.is_service_artist ? "BookTalent service artist" : "Normal artist"],
                  ["Category", selected.category],
                  ["City", selected.city],
                  ["Events done", selected.events_done || 0],
                  ["Account", selected.user?.deleted ? "Deleted" : selected.user?.suspended ? "Suspended" : "Active"],
                ]} />
              </DrawerSection>
              <DrawerSection title="Readiness">
                <DrawerFacts items={[
                  ["Profile", `${completion.percent || 0}%`],
                  ["Go-live", `${goLive.completed_steps}/${goLive.total_steps} steps${goLive.is_live ? " · live" : ""}`],
                ]} />
                {(completion.missing || []).length > 0 && (
                  <>
                    <div className="text-muted fs-12 mt-12">Missing</div>
                    <ul className="admin-drawer-missing">{completion.missing.map((item) => <li key={item}>{item}</li>)}</ul>
                  </>
                )}
              </DrawerSection>
              <DrawerSection title="Actions">
                <div className="admin-drawer-actions">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => openDialog(setEditing, selected)} data-testid={`edit-${selected.user_id}`}>Edit artist</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => feature(selected.user_id)} data-testid={`feature-${selected.user_id}`}>{selected.is_featured ? "Unfeature" : "Feature on homepage"}</button>
                  {selected.is_boosted && <button type="button" className="btn btn-amber btn-sm" onClick={() => removeBoost(selected)} data-testid={`unboost-${selected.user_id}`}>Remove Boosted flag</button>}
                  {completion.percent < 100 && selected.user?.email && !selected.user?.deleted && (
                    <a className="btn btn-ghost btn-sm" href={artistReminderMailto(selected)} data-testid={`contact-profile-completion-${selected.user_id}`}>Email to complete profile</a>
                  )}
                  {selected.user?.deleted ? (
                    <button type="button" className="btn btn-green btn-sm" onClick={() => openDialog(setRestoring, { id: selected.user_id, label: selected.stage_name, deactivated_email: selected.user.deactivated_email })} data-testid={`restore-${selected.user_id}`}>Restore account</button>
                  ) : (
                    <button type="button" className={`btn btn-sm ${selected.user?.suspended ? "btn-green" : "btn-amber"}`} onClick={() => suspend(selected.user_id)} data-testid={`suspend-${selected.user_id}`}>
                      {selected.user?.suspended ? "Unsuspend" : "Suspend"}
                    </button>
                  )}
                  <button type="button" className="btn btn-red btn-sm" onClick={() => openDialog(setDeleting, { id: selected.user_id, label: selected.stage_name })} data-testid={`delete-${selected.user_id}`}>Delete artist</button>
                </div>
              </DrawerSection>
            </>
          );
        })()}
      </AdminDetailDrawer>
      {editing && (
        <UserEditModal
          user={editing.user}
          profile={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(page); toast("Saved"); }}
          toast={toast}
        />
      )}
      {deleting && (
        <UserDeleteModal
          target={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); reload(page); }}
          toast={toast}
        />
      )}
      {restoring && (
        <UserRestoreModal
          target={restoring}
          onClose={() => setRestoring(null)}
          onDone={() => { setRestoring(null); reload(page); }}
          toast={toast}
        />
      )}
    </div>
  );
}

// Friendly labels + pill colours for booking statuses (admin lists).
const BOOKING_STATUS_INFO = {
  pending_payment: ["Awaiting payment", "pill-amber"],
  pending_artist: ["Awaiting artist", "pill-amber"],
  confirmed: ["Confirmed", "pill-blue"],
  started: ["In progress", "pill-blue"],
  completed_by_artist: ["Artist marked done", "pill-purple"],
  completed: ["Booking done", "pill-green"],
  reviewed: ["Reviewed", "pill-green"],
  cancelled: ["Cancelled", "pill-red"],
  rejected: ["Rejected", "pill-red"],
  auto_expired: ["Expired", "pill-red"],
};
const bookingStatusInfo = (status) => BOOKING_STATUS_INFO[status] || [String(status || "Unknown").replace(/_/g, " "), "pill-purple"];

const BOOKING_FLAG_INFO = {
  expired: { label: "Expired", className: "pill-red", help: "Auto-expired or still awaiting action after the event date." },
  event_done: { label: "Event done", className: "pill-blue", help: "Event date passed or completion was submitted." },
  completed: { label: "Booking done", className: "pill-green", help: "Completion approved." },
  payout_pending: { label: "Payout pending", className: "pill-amber", help: "Event done, customer payment recorded, artist payout not marked paid." },
};

const fmtShortDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// The API falls back to the artist id when the artist record is gone.
const bookingArtistName = (booking) => (
  !booking.artist_name || booking.artist_name === booking.artist_id ? "Artist removed" : booking.artist_name
);

function AdminBookings() {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState("");
  const [flag, setFlag] = useState("");
  const [statusCounts, setStatusCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [scrollTop, setScrollTop] = useState(0);
  const tableScrollRef = useRef(null);

  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    setScrollTop(0);
    let active = true;
    api.get("/admin/bookings", {
      params: { page, limit: pageSize, ...(status ? { status } : {}), ...(flag ? { flag } : {}) },
    })
      .then((r) => {
        if (!active) return;
        const payload = r.data || {};
        const items = Array.isArray(payload.items) ? payload.items : Array.isArray(payload) ? payload : [];
        setList(items);
        setStatusCounts(payload.status_counts || {});
        setTotal(Number(payload.total || items.length || 0));
        setPages(Number(payload.pages || Math.max(1, Math.ceil((payload.total || items.length || 0) / pageSize))));
      })
      .catch(() => {
        if (!active) return;
        setList([]);
        setTotal(0);
        setPages(1);
      });
    return () => { active = false; };
  }, [page, pageSize, status, flag]);

  useEffect(() => {
    setPage((current) => Math.min(current, pages || 1));
  }, [pages]);

  // Iter 65 — booking notification click → highlight the correct row.
  useHighlightRow({ prefix: "booking-row", dataKey: list.length });
  const virtualRows = getVirtualWindow(scrollTop, 64, 640, list.length);
  const visibleBookings = list.slice(virtualRows.start, virtualRows.end);
  const allCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);
  const selectedPayment = selected ? summarizeBookingPayment(selected, selected.payment_schedule || selected.schedule) : null;

  return (
    <div className="card" data-testid="admin-bookings">
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <div>
          <div className="card-title">All Bookings ({total || list.length})</div>
          <div className="text-muted fs-11">Click a booking to see payments, flags and actions.</div>
        </div>
        <div className="flex items-center gap-12" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <label className="flex items-center gap-8 fs-12 text-muted">
            Booking flag
            <select
              className="field-input"
              value={flag}
              onChange={(event) => { setFlag(event.target.value); setPage(1); }}
              style={{ width: 170, padding: "var(--space-1) 8px" }}
              data-testid="admin-bookings-flag-filter"
            >
              <option value="">All flags</option>
              {Object.entries(BOOKING_FLAG_INFO).map(([key, info]) => <option key={key} value={key}>{info.label}</option>)}
            </select>
          </label>
          <label className="admin-page-size">
            <span>Rows per page</span>
            <select
              className="field-input"
              value={pageSize}
              onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}
              data-testid="admin-bookings-page-size"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
        </div>
      </div>
      <div className="admin-status-chips" role="group" aria-label="Filter by status" data-testid="admin-bookings-status-filter">
        <button type="button" className={`admin-status-chip ${!status ? "active" : ""}`} onClick={() => { setStatus(""); setPage(1); }} aria-pressed={!status}>
          All <span>{allCount}</span>
        </button>
        {Object.keys(BOOKING_STATUS_INFO).filter((key) => statusCounts[key] || status === key).map((key) => (
          <button
            key={key}
            type="button"
            className={`admin-status-chip ${status === key ? "active" : ""}`}
            onClick={() => { setStatus(key); setPage(1); }}
            aria-pressed={status === key}
            data-testid={`admin-bookings-status-${key}`}
          >
            {bookingStatusInfo(key)[0]} <span>{statusCounts[key] || 0}</span>
          </button>
        ))}
      </div>
      <div
        className="table-wrap admin-virtual-table-scroll admin-bookings-virtual-scroll"
        ref={tableScrollRef}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        <table className="table admin-virtual-table admin-bookings-virtual-table">
          <thead><tr><th>Booking</th><th>Customer → Artist</th><th>Event</th><th>Payment</th><th>Status</th></tr></thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="text-muted" style={{ textAlign: "center", padding: "var(--space-6)" }}>No bookings match these filters.</td></tr>
            )}
            {virtualRows.topPadding > 0 && (
              <tr className="admin-virtual-spacer" aria-hidden="true">
                <td colSpan={5} style={{ height: virtualRows.topPadding, "--virtual-spacer-height": `${virtualRows.topPadding}px` }} />
              </tr>
            )}
            {visibleBookings.map((b) => {
              const payment = summarizeBookingPayment(b, b.payment_schedule || b.schedule);
              const [statusLabel, statusClass] = bookingStatusInfo(b.status);
              // Skip flags that just repeat the status (e.g. status "Expired" + flag "Expired").
              const lifecycleFlags = (b.lifecycle_flags || []).filter((item) => BOOKING_FLAG_INFO[item] && BOOKING_FLAG_INFO[item].label !== statusLabel);
              const paidPct = payment.total > 0 ? Math.min(100, Math.round((payment.paid / payment.total) * 100)) : 0;
              return (
              <tr
                key={b.id}
                id={`booking-row-${b.id}`}
                data-testid={`booking-row-${b.id}`}
                className={`admin-compact-row ${selected?.id === b.id ? "is-selected" : ""}`}
                tabIndex={0}
                onClick={() => setSelected(b)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelected(b); } }}
              >
                <td>
                  <div className="admin-cell-main font-mono text-gold fs-12">{b.ref}</div>
                  <div className="admin-cell-sub">{b.event_type || "Event"}</div>
                </td>
                <td>
                  <div className="admin-cell-main">{b.customer_name || "Customer"}</div>
                  <div className="admin-cell-sub">→ {bookingArtistName(b)}</div>
                </td>
                <td>
                  <div className="admin-cell-main fw-400">{fmtShortDate(b.event_date)}</div>
                  <div className="admin-cell-sub">{b.city || "—"}</div>
                </td>
                <td>
                  <div className="admin-cell-main">{fmtMoneyNumber(payment.total)}</div>
                  <div className="admin-mini-bar" aria-label={`${paidPct}% paid`}><span style={{ width: `${paidPct}%`, background: "var(--green)" }} /></div>
                  <div className={`admin-cell-sub ${payment.due > 0 ? "text-amber" : ""}`}>{payment.due > 0 ? `${fmtMoneyNumber(payment.due)} due` : "Fully paid"}</div>
                </td>
                <td data-testid={`admin-booking-flags-${b.id}`}>
                  <span className={`pill ${statusClass}`}>{statusLabel}</span>
                  {lifecycleFlags.length > 0 && (
                    <div
                      className="admin-cell-sub"
                      style={{ marginTop: 4 }}
                      title={lifecycleFlags.map((item) => `${BOOKING_FLAG_INFO[item].label}: ${BOOKING_FLAG_INFO[item].help}`).join(" · ")}
                    >
                      <span className={`pill ${BOOKING_FLAG_INFO[lifecycleFlags[0]].className}`} style={{ fontSize: 10 }}>
                        {BOOKING_FLAG_INFO[lifecycleFlags[0]].label}
                      </span>
                      {lifecycleFlags.length > 1 && <span className="fs-11"> +{lifecycleFlags.length - 1} more</span>}
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
            {virtualRows.bottomPadding > 0 && (
              <tr className="admin-virtual-spacer" aria-hidden="true">
                <td colSpan={5} style={{ height: virtualRows.bottomPadding, "--virtual-spacer-height": `${virtualRows.bottomPadding}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <AdminDetailDrawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        testId="admin-booking-drawer"
        title={selected?.ref}
        subtitle={selected ? `${selected.event_type || "Event"} · ${fmtShortDate(selected.event_date)}` : ""}
        badge={selected && <span className={`pill ${bookingStatusInfo(selected.status)[1]}`}>{bookingStatusInfo(selected.status)[0]}</span>}
        footer={selected && (
          <>
            <Link to={`/bookings/${selected.id}`} className="btn btn-gold btn-sm" data-testid={`admin-view-booking-${selected.id}`}>Open full booking</Link>
            {selected.artist_id && bookingArtistName(selected) !== "Artist removed" && (
              <Link to={`/staff/artists/${selected.artist_id}`} className="btn btn-ghost btn-sm">Review artist</Link>
            )}
          </>
        )}
      >
        {selected && (
          <>
            <DrawerSection title="Event">
              <DrawerFacts items={[
                ["Type", selected.event_type],
                ["Date", fmtShortDate(selected.event_date)],
                ["Venue", selected.venue],
                ["City", selected.city],
              ]} />
            </DrawerSection>
            <DrawerSection title="People">
              <DrawerFacts items={[["Customer", selected.customer_name], ["Artist", bookingArtistName(selected)]]} />
            </DrawerSection>
            <DrawerSection title="Payment">
              <DrawerFacts items={[
                ["Total", fmtMoneyNumber(selectedPayment.total)],
                ["Paid by customer", <span className="text-green fw-600">{fmtMoneyNumber(selectedPayment.paid)}</span>],
                ["Customer due", <span className={selectedPayment.due > 0 ? "text-amber fw-600" : ""}>{fmtMoneyNumber(selectedPayment.due)}</span>],
                ["Next payment", selectedPayment.nextMilestone
                  ? `${fmtINRFull(selectedPayment.nextMilestone.amount)} · ${formatPaymentDueLabel(selectedPayment.nextMilestone, selectedPayment.event_date)}`
                  : selectedPayment.finalMilestone ? "Complete" : "Not scheduled"],
                ["Final payment due", selectedPayment.finalMilestone
                  ? `${selectedPayment.finalMilestone.due_date || "At booking"} · ${selectedPayment.finalMilestone.label}`
                  : "Not scheduled"],
              ]} />
            </DrawerSection>
            {(selected.lifecycle_flags || []).some((item) => BOOKING_FLAG_INFO[item]) && (
              <DrawerSection title="Booking flags">
                <ul className="admin-flag-explainer">
                  {(selected.lifecycle_flags || []).filter((item) => BOOKING_FLAG_INFO[item]).map((item) => (
                    <li key={item}><span className={`pill ${BOOKING_FLAG_INFO[item].className}`}>{BOOKING_FLAG_INFO[item].label}</span>{BOOKING_FLAG_INFO[item].help}</li>
                  ))}
                </ul>
              </DrawerSection>
            )}
            {selected.status === "cancelled" && (
              <DrawerSection title="Cancellation">
                <div data-testid={`admin-cancel-info-${selected.id}`} className="fs-13">
                  <span className="text-muted">By {selected.cancelled_by || "—"}:</span> {selected.cancel_reason || "No reason given"}
                </div>
              </DrawerSection>
            )}
          </>
        )}
      </AdminDetailDrawer>
      <div className="admin-list-pagination">
        <span className="text-muted fs-12" data-testid="admin-bookings-page-summary">
          {total ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString("en-IN")}` : "No bookings"}
        </span>
        {pages > 1 && (
          <Pagination className="admin-list-pagination-controls">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page <= 1}
                  onClick={(event) => { event.preventDefault(); if (page > 1) setPage(page - 1); }}
                  data-testid="admin-bookings-prev"
                />
              </PaginationItem>
              <PaginationItem><span className="cd-page-indicator" aria-current="page">Page {page} of {pages}</span></PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page >= pages}
                  onClick={(event) => { event.preventDefault(); if (page < pages) setPage(page + 1); }}
                  data-testid="admin-bookings-next"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}

function AdminKYC({ toast, onNavigateToRequest }) {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [documentViewer, setDocumentViewer] = useState(null);
  const [approveState, setApproveState] = useState(null); // { artist_id, name }
  const [aboutMap, setAboutMap] = useState({});
  const [profileDetails, setProfileDetails] = useState({});
  const loadSequence = useRef(0);
  const reload = useCallback(() => {
    const requestId = ++loadSequence.current;
    return api.get(`/admin/kyc?status=${status}&page=${page}&limit=${pageSize}`).then((r) => {
      if (requestId !== loadSequence.current) return;
      if (r.data.page !== page) setPage(r.data.page);
      setList(r.data.items);
      setTotal(r.data.total);
      setPages(Math.max(1, r.data.pages));
    }).catch((error) => {
      if (requestId !== loadSequence.current) return;
      toast(formatApiError(error), "error");
      setList([]);
      setTotal(0);
      setPages(1);
    });
  }, [page, pageSize, status, toast]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (!expanded) return;
    if (aboutMap[expanded]) return;
    api.get(`/artists/${expanded}/about`).then((r) => {
      setAboutMap((m) => ({ ...m, [expanded]: r.data || null }));
    }).catch(() => {
      setAboutMap((m) => ({ ...m, [expanded]: null }));
    });
  }, [expanded, aboutMap]);
  useEffect(() => {
    if (!expanded || profileDetails[expanded]) return;
    setProfileDetails((details) => ({ ...details, [expanded]: { loading: true } }));
    api.get(`/admin/kyc/${expanded}/profile`).then((r) => {
      setProfileDetails((details) => ({ ...details, [expanded]: { data: r.data } }));
    }).catch((e) => {
      setProfileDetails((details) => ({
        ...details,
        [expanded]: { error: formatApiError(e) },
      }));
    });
  }, [expanded, profileDetails]);

  const decide = async (artist_id, decision, extra = {}) => {
    // Iter 98 — Approve now routes through a modal that captures
    // artist_type + percentage_deal so the commercial deal is set at
    // the moment of approval (matching Sec 4 of the business concept).
    const row = list.find((k) => k.user_id === artist_id);
    if (decision === "approve" && row?.flow_version === "declaration_v1") extra = { ...extra, _skipModal: true };
    if (decision === "approve" && !extra._skipModal) {
      setApproveState({
        artist_id,
        name: `${row?.user?.first_name || ""} ${row?.user?.last_name || ""}`.trim() || "Artist",
        artist_profile: row?.artist_profile || {},
      });
      return;
    }
    let reason = "";
    if (decision === "reject" || decision === "request_resubmission") {
      reason = window.prompt(`Reason for ${decision === "reject" ? "rejection" : "resubmission"}:`, "") || "";
      if (!reason.trim()) { toast("Reason is required", "error"); return; }
    }
    try {
      await api.post("/admin/kyc/decide", { artist_id, decision, reason, ...extra });
      toast(`KYC ${decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "resubmission requested"}`);
      reload();
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  return (
    <div className="card" data-testid="admin-kyc">
      <div className="card-head" style={{ justifyContent: "space-between", display: "flex", alignItems: "center" }}>
        <div className="card-title"> KYC Queue ({total.toLocaleString("en-IN")})</div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="input" style={{ width: 200 }} data-testid="kyc-status-filter">
          <option value="pending">Pending</option>
          <option value="needs_resubmission">Needs Resubmission</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      </div>
      <div style={{ padding: "var(--space-3)" }}>
        {list.length === 0 && <div className="empty"><div className="empty-icon"></div><div className="empty-title">No KYC records</div></div>}
        <VirtualizedList
          items={list}
          getKey={(item) => item.user_id}
          estimatedItemHeight={144}
          testId="kyc-virtual-list"
          resetKey={`${page}:${status}`}
          renderItem={(k) => {
          const isOpen = expanded === k.user_id;
          return (
            <div className="card admin-virtual-item-card" data-testid={`kyc-row-${k.user_id}`}>
              <div className="flex items-center gap-16">
                <div className="avatar">{k.user?.first_name?.[0]}</div>
                <div style={{ flex: 1 }}>
                  <div className="fw-600">
                    {k.user?.first_name} {k.user?.last_name}
                    {k.artist_profile?.stage_name && <span className="text-muted fs-12" style={{ marginLeft: 8 }}>· {k.artist_profile.stage_name}</span>}
                  </div>
                  <div className="text-muted fs-12">
                    {k.user?.email} · Submitted {k.submitted_at?.slice(0, 10)} ·{" "}
                    <span className={`pill pill-${k.status === "approved" ? "green" : k.status === "rejected" ? "red" : "amber"}`}>{k.status}</span>
                    {k.artist_profile?.category_pending && (
                      <span className="pill pill-amber" style={{ marginLeft: 4 }} data-testid={`kyc-pending-category-${k.user_id}`}>
                        Category review: {k.artist_profile.pending_category_name || k.artist_profile.category || "pending"}
                      </span>
                    )}
                    {k.artist_profile?.city_pending && (
                      <span className="pill pill-amber" style={{ marginLeft: 4 }} data-testid={`kyc-pending-city-${k.user_id}`}>
                        City review: {k.artist_profile.pending_city_name || k.artist_profile.city || "pending"}
                      </span>
                    )}
                    {/* Iter 90 — v2 state visibility */}
                    {k.v2_status && k.v2_status !== k.status && (
                      <span className="pill pill-gold" style={{ marginLeft: 4, fontSize: 10 }} title="Post-approval v2 state" data-testid={`kyc-v2-${k.user_id}`}>
                        {k.v2_status}
                      </span>
                    )}
                  </div>
                  <div className="text-muted fs-11 mt-4">Docs: {Object.keys(k.documents || {}).join(", ") || "—"}{k.pan_number && ` · PAN ${k.pan_number}`}{k.aadhaar_number_masked && ` · Aadhaar ${k.aadhaar_number_masked}`}</div>
                </div>
                <Link
                  className="btn btn-gold btn-sm"
                  to={`/staff/artists/${k.user_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open the artist's full profile, questionnaire and go-live progress in a new tab"
                  data-testid={`kyc-review-${k.user_id}`}
                >Review full profile</Link>
                <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(isOpen ? null : k.user_id)} data-testid={`kyc-view-${k.user_id}`}>{isOpen ? "Hide" : "View"}</button>
                {k.status === "pending" || k.status === "needs_resubmission" ? (
                  <>
                    <button className="btn btn-green btn-sm" onClick={() => decide(k.user_id, "approve")} data-testid={`kyc-approve-${k.user_id}`}>✓ Approve</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => decide(k.user_id, "request_resubmission")} data-testid={`kyc-resub-${k.user_id}`}>↻ Resubmit</button>
                    <button className="btn btn-red btn-sm" onClick={() => decide(k.user_id, "reject")} data-testid={`kyc-reject-${k.user_id}`}>✕ Reject</button>
                  </>
                ) : null}
                {/* Iter 90b — Agreement viewer for artists past T&C acceptance */}
                {["agreement_generated", "live"].includes(k.v2_status) && (
                  <>
                    <a
                      className="btn btn-ghost btn-sm"
                      href={`${api.defaults.baseURL}/admin/agreements/${k.user_id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`kyc-agreement-view-${k.user_id}`}
                      title="Open signed agreement PDF"
                    > View Agreement</a>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={async () => {
                        if (!window.confirm("Create a revised agreement preview? The artist must review and accept it before going live again; the existing accepted copy remains in history.")) return;
                        try {
                          await api.post(`/admin/agreements/${k.user_id}/reissue`);
                          toast("A revised agreement preview is ready. The artist must review and accept it before going live again.", "success");
                          reload();
                        } catch (e) { toast(formatApiError(e), "error"); }
                      }}
                      data-testid={`kyc-agreement-reissue-${k.user_id}`}
                      title="Create a revised agreement preview"
                    >↻ Re-issue</button>
                  </>
                )}
              </div>
              {isOpen && (
                <div className="card card-pad mt-12" style={{ marginTop: "var(--space-3)" }}>
                  <div className="fw-700 mb-12">KYC review summary</div>
                  <div className="grid grid-3 gap-12 mb-16">
                    <div>
                      <div className="text-muted fs-11 mb-4">Identity</div>
                      <div className="fs-13"><strong>Name:</strong> {k.full_name || `${k.user?.first_name || ""} ${k.user?.last_name || ""}`.trim() || "—"}</div>
                      <div className="fs-13"><strong>DOB:</strong> {k.dob || "—"}</div>
                      <div className="fs-13"><strong>PAN:</strong> {k.pan_number || "—"}</div>
                      <div className="fs-13"><strong>Aadhaar:</strong> {k.aadhaar_number_masked || "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted fs-11 mb-4">Artist profile</div>
                      <div className="fs-13"><strong>Stage name:</strong> {k.artist_profile?.stage_name || "—"}</div>
                      <div className="fs-13"><strong>Category:</strong> {k.artist_profile?.category || "—"}</div>
                      <div className="fs-13"><strong>City:</strong> {k.artist_profile?.city || "—"}</div>
                      <div className="fs-13"><strong>Email:</strong> {k.user?.email || "—"}</div>
                      <div className="fs-13"><strong>Phone:</strong> {k.user?.phone || "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted fs-11 mb-4">Decision</div>
                      <div className="fs-13"><strong>Status:</strong> {k.status || "—"}</div>
                      <div className="fs-13"><strong>Submitted:</strong> {k.submitted_at ? new Date(k.submitted_at).toLocaleString() : "—"}</div>
                      <div className="fs-13"><strong>Decision by:</strong> {k.decided_by || "—"}</div>
                      <div className="fs-13"><strong>Reason:</strong> {k.reason || "—"}</div>
                    </div>
                  </div>

                  {k.flow_version === "declaration_v1" && (
                    <div className="card card-pad mb-16" data-testid={`kyc-declaration-review-${k.user_id}`}>
                      <div className="fw-600 mb-8">Declaration evidence · fixed 15% commission</div>
                      <div className="text-muted fs-12" style={{ whiteSpace: "pre-wrap" }}>{k.declaration_script?.text}</div>
                      <div className="fs-12 mt-8">Script version {k.declaration_script?.version || "—"}</div>
                      <div className="mt-8" data-testid={`kyc-declaration-ticks-${k.user_id}`}>
                        {[
                          ["accepts_booktalent_commission", "15% BookTalent commission/service charge"],
                          ["confirms_payment_details_accurate", "Payment details and uploaded documents are accurate"],
                          ["confirms_lowest_platform_rates", "BookTalent rates are the artist’s lowest across platforms"],
                        ].map(([key, label]) => {
                          const record = k.declarations?.[key];
                          return <div key={key} className="fs-12" style={{ marginTop: 5 }}>
                            <strong style={{ color: record?.accepted ? "#6ee7a8" : "#f87171" }}>{record?.accepted ? "Accepted" : "Missing"}</strong>
                            {" · "}{label}{record?.accepted_at ? ` · ${new Date(record.accepted_at).toLocaleString()}` : ""}
                          </div>;
                        })}
                      </div>
                      {k.declaration_video_id && <video controls style={{ width: "100%", marginTop: 10, maxHeight: 360 }} src={`${api.defaults.baseURL}/media/${k.declaration_video_id}`} />}
                    </div>
                  )}
                  <div className="card card-pad mb-16" data-testid={`kyc-agreement-status-${k.user_id}`}>
                    <div className="fw-600 mb-8">Agreement acceptance record</div>
                    <div className="grid grid-2 gap-12">
                      <div>
                        <div className="text-muted fs-11 mb-4">Accepted agreement</div>
                        <div className="fs-13">
                          <strong>Accepted:</strong>{" "}
                          {k.artist_profile?.tnc_accepted_at || k.artist_profile?.artist_agreement_accepted_at ? "Yes" : "No"}
                        </div>
                        <div className="fs-13">
                          <strong>Accepted at:</strong>{" "}
                          {k.artist_profile?.artist_agreement_accepted_at || k.artist_profile?.tnc_accepted_at
                            ? new Date(k.artist_profile.artist_agreement_accepted_at || k.artist_profile.tnc_accepted_at).toLocaleString()
                            : "—"}
                        </div>
                        <div className="fs-13">
                          <strong>Agreement version:</strong>{" "}
                          {k.artist_profile?.artist_agreement_version || "—"}
                        </div>
                        <div className="fs-13">
                          <strong>Agreement SHA-256:</strong>{" "}
                          <span className="font-mono fs-11">{k.artist_profile?.artist_agreement_sha256 || "Legacy record"}</span>
                        </div>
                        <div className="fs-13">
                          <strong>Platform Terms version:</strong>{" "}
                          {k.artist_profile?.artist_terms_version || "—"}
                        </div>
                        <div className="fs-13">
                          <strong>Agreement generated:</strong>{" "}
                          {k.artist_profile?.agreement_generated_at
                            ? new Date(k.artist_profile.agreement_generated_at).toLocaleString()
                            : "—"}
                        </div>
                        <div className="fs-13">
                          <strong>Agreement ID:</strong>{" "}
                          {k.artist_profile?.agreement_id || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted fs-11 mb-4">Current status</div>
                        <div className="fs-13"><strong>KYC:</strong> {k.artist_profile?.kyc_status || k.v2_status || "—"}</div>
                        <div className="fs-13"><strong>Platform Terms SHA-256:</strong>{" "}
                          <span className="font-mono fs-11">{k.artist_profile?.artist_terms_sha256 || "—"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-muted fs-12 mt-8">
                      The full Artist Agreement is generated and becomes available after KYC approval, when the artist accepts the Terms &amp; Conditions.
                    </div>
                  </div>

                  {aboutMap[k.user_id] && (
                    <div>
                      <div className="fw-600 mb-8">Questionnaire answers</div>
                      <div className="grid grid-2 gap-16">
                        {(["universal", "category"]).map((key) => {
                          const items = aboutMap[k.user_id]?.[key] || [];
                          if (!items.length) return null;
                          return (
                            <div key={key} className="card card-pad" style={{ background: "rgba(255,255,255,0.02)" }}>
                              <div className="fw-600 mb-8" style={{ textTransform: "capitalize" }}>
                                {key === "universal" ? "Profile details" : "Category details"}
                              </div>
                              {items.map((item, idx) => (
                                <div key={`${key}-${idx}`} style={{ marginBottom: "var(--space-2)" }}>
                                  <div className="text-muted fs-11 mb-4">{item.question}</div>
                                  <div className="fs-13">
                                    {Array.isArray(item.answer)
                                      ? item.answer.join(", ")
                                      : typeof item.answer === "object"
                                        ? JSON.stringify(item.answer)
                                        : String(item.answer || "—")}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {profileDetails[k.user_id]?.loading && (
                    <div className="text-muted fs-12 mt-12">Loading full artist profile and packages…</div>
                  )}
                  {profileDetails[k.user_id]?.error && (
                    <div className="text-muted fs-12 mt-12" role="alert">
                      Could not load the full artist profile: {profileDetails[k.user_id].error}
                    </div>
                  )}
                  {profileDetails[k.user_id]?.data && (() => {
                    const { profile = {}, packages = [], media = [] } = profileDetails[k.user_id].data;
                    const profileFields = Object.entries(profile).filter(([key, value]) =>
                      key !== "answers" && key !== "user_id" && value !== null && value !== "",
                    );
                    return (
                      <div className="mt-16" data-testid={`kyc-profile-${k.user_id}`}>
                        <div className="fw-600 mb-8">Full artist profile</div>
                        {profileFields.length > 0 ? (
                          <div className="grid grid-3 gap-12 mb-16">
                            {profileFields.map(([key, value]) => (
                              <div key={key}>
                                <div className="text-muted fs-11 mb-4">
                                  {key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}
                                </div>
                                <div className="fs-13">
                                  {Array.isArray(value)
                                    ? value.join(", ")
                                    : typeof value === "object"
                                      ? JSON.stringify(value)
                                      : String(value)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        <div className="fw-600 mb-8" data-testid={`kyc-package-count-${k.user_id}`}>
                          Performance packages ({packages.length})
                        </div>
                        {packages.length > 0 ? (
                          <div className="grid grid-2 gap-12 mb-16">
                            {packages.map((pkg, index) => {
                              const packageFields = Object.entries(pkg).filter(([key, value]) =>
                                !["id", "artist_id", "created_at", "updated_at"].includes(key) &&
                                value !== null && value !== "",
                              );
                              return (
                                <div key={pkg.id || index} className="card card-pad">
                                  <div className="fw-600 mb-8">
                                    {pkg.name || pkg.title || `Package ${index + 1}`}
                                    {pkg.price != null && <span className="text-muted"> · {fmtINRFull(pkg.price)}</span>}
                                  </div>
                                  {packageFields.map(([key, value]) => (
                                    <div key={key} className="fs-12 mb-4">
                                      <strong>{key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}:</strong>{" "}
                                      {Array.isArray(value)
                                        ? value.join(", ")
                                        : typeof value === "object"
                                          ? JSON.stringify(value)
                                          : String(value)}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-muted fs-12 mb-16">No packages have been created yet.</div>
                        )}

                        {media.length > 0 && (
                          <>
                            <div className="fw-600 mb-8">Profile media ({media.length})</div>
                            <div className="grid grid-3 gap-12 mb-16">
                              {media.map((item) => (
                                <a
                                  key={item.id}
                                  href={`${api.defaults.baseURL}/media/${item.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="card card-pad"
                                  style={{ textDecoration: "none" }}
                                >
                                  <div className="text-muted fs-11 mb-4">
                                    {item.title || item.type || "Media"}
                                  </div>
                                  <img
                                    src={`${api.defaults.baseURL}/media/${item.id}/thumb`}
                                    alt={item.title || item.type || "Artist media"}
                                    style={{ maxWidth: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8 }}
                                  />
                                </a>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  <div className="mt-16">
                    <div className="fw-600 mb-8">Documents</div>
                    <div className="grid grid-3 gap-12" data-testid={`kyc-docs-${k.user_id}`}>
                      {Object.entries(k.documents || {}).map(([field, mid]) => (
                        <div key={field} className="card card-pad" style={{ textAlign: "center" }}>
                          <div className="text-muted fs-11 mb-4" style={{ marginBottom: "var(--space-1)" }}>{field.toUpperCase()}</div>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setDocumentViewer({
                              id: mid,
                              field,
                              artist: `${k.user?.first_name || ""} ${k.user?.last_name || ""}`.trim() || k.artist_profile?.stage_name || "Artist",
                              artist_id: k.user_id,
                              review: {
                                name: k.full_name || `${k.user?.first_name || ""} ${k.user?.last_name || ""}`.trim(),
                                stage_name: k.artist_profile?.stage_name,
                                category: k.artist_profile?.category,
                                city: k.artist_profile?.city,
                                email: k.user?.email,
                                status: k.status,
                                submitted_at: k.submitted_at,
                                decided_by: k.decided_by,
                                reason: k.reason,
                                dob: k.dob,
                                pan_number: k.pan_number,
                                aadhaar_number_masked: k.aadhaar_number_masked,
                                about: aboutMap[k.user_id],
                              },
                            })}
                            data-testid={"kyc-doc-" + k.user_id + "-" + field}
                          >
                            <img src={`${api.defaults.baseURL}/media/${mid}/thumb`} alt={field} style={{ maxWidth: "100%", borderRadius: 8 }} onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML += '<div style="font-size:48px"></div>'; }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {k.reason && (k.status === "rejected" || k.status === "needs_resubmission") && (
                <div className="text-muted fs-12 mt-8" style={{ marginTop: "var(--space-2)" }}>Reason: {k.reason}</div>
              )}
            </div>
          );
        }}
        />
      </div>
      <AdminListPagination
        page={page}
        pages={pages}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        testId="kyc"
        itemLabel="KYC records"
      />
      {approveState && (
        <KycApproveModal
          artist={approveState}
          onNavigateToRequest={onNavigateToRequest}
          onCancel={() => setApproveState(null)}
          onConfirm={async ({ artist_type, percentage_deal }) => {
            setApproveState(null);
            await decide(approveState.artist_id, "approve", {
              _skipModal: true, artist_type, percentage_deal,
            });
          }}
        />
      )}
      {documentViewer && (
        <KycDocumentViewer document={documentViewer} onClose={() => setDocumentViewer(null)} />
      )}
    </div>
  );
}

function AdminAgreements() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ page: String(page), limit: "50" });
    if (query.trim()) params.set("q", query.trim());
    api.get(`/admin/agreements?${params.toString()}`)
      .then((response) => {
        if (!active) return;
        setRows(response.data?.items || []);
        setPages(response.data?.pages || 1);
      })
      .catch((requestError) => {
        if (active) setError(formatApiError(requestError));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, query]);

  const openDocument = async (artist, agreement, inline) => {
    const popup = inline ? window.open("", "_blank") : null;
    if (popup) popup.opener = null;
    try {
      const params = new URLSearchParams({ agreement_id: agreement.id });
      if (inline) params.set("inline", "true");
      const response = await api.get(`/admin/agreements/${artist.artist_id}/download?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      if (popup) popup.location.href = url;
      else {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `BookTalent-${agreement.ref || "artist-agreement"}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (requestError) {
      if (popup) popup.close();
      setError(formatApiError(requestError));
    }
  };

  return (
    <div className="card" data-testid="admin-agreements">
      <div className="card-head">
        <div className="card-title">Artist Agreements</div>
        <span className="text-muted fs-12">View accepted records, previews, and historical versions.</span>
      </div>
      <div className="flex items-center gap-8" style={{ padding: "var(--space-3)" }}>
        <Search size={16} aria-hidden="true" />
        <input
          className="input"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setPage(1); }}
          placeholder="Search artist name or email"
          aria-label="Search artist agreements"
          data-testid="admin-agreements-search"
          style={{ maxWidth: 420 }}
        />
      </div>
      {error && <div role="alert" className="text-red fs-12" style={{ padding: "0 var(--space-3) var(--space-3)" }}>{error}</div>}
      {loading ? <div className="text-muted fs-13" style={{ padding: "var(--space-4)" }}>Loading artist agreements…</div> : (
        <div className="table-wrap">
          <table className="table" data-testid="admin-agreements-table">
            <thead><tr><th>Artist</th><th>KYC status</th><th>Agreement records</th></tr></thead>
            <tbody>
              {rows.map((artist) => (
                <tr key={artist.artist_id} data-testid={`admin-agreement-artist-${artist.artist_id}`}>
                  <td>
                    <div className="fw-600">{artist.artist_name}</div>
                    <div className="text-muted fs-11">{artist.artist_email || "—"}</div>
                  </td>
                  <td><span className="pill pill-blue">{artist.kyc_status || "—"}</span></td>
                  <td>
                    {artist.agreements?.length ? (
                      <div className="flex" style={{ flexDirection: "column", gap: 8 }}>
                        {artist.agreements.map((agreement) => (
                          <div key={agreement.id} className="flex justify-between items-center gap-8" style={{ borderBottom: "1px solid var(--glass-border)", paddingBottom: 6 }}>
                            <div>
                              <div className="fw-600 fs-12">
                                {agreement.ref || "Agreement"}
                                {agreement.is_current && <span className="pill pill-green" style={{ marginLeft: 6 }}>Current</span>}
                                <span className={`pill ${agreement.status === "accepted" ? "pill-green" : "pill-amber"}`} style={{ marginLeft: 6 }}>
                                  {agreement.status || "legacy"}
                                </span>
                              </div>
                              <div className="text-muted fs-11">
                                Agreement {agreement.canonical_version || "legacy"} · Platform Terms {agreement.terms_version || "legacy"} · {agreement.accepted_at ? `Accepted ${agreement.accepted_at.slice(0, 10)}` : "Not accepted"}
                                {agreement.commercial?.is_service_artist
                                  ? ` · ${agreement.commercial.percentage_deal}% commission`
                                  : agreement.commercial
                                    ? " · No artist commission · 5% customer fee"
                                    : ""}
                              </div>
                            </div>
                            <div className="flex gap-4">
                              <button type="button" className="btn btn-ghost btn-xs" onClick={() => openDocument(artist, agreement, true)}>View</button>
                              <button type="button" className="btn btn-ghost btn-xs" onClick={() => openDocument(artist, agreement, false)}>Download</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-muted fs-12">No agreement available</span>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={3} className="text-muted">No artist records found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-between items-center" style={{ padding: "var(--space-3)" }}>
        <span className="text-muted fs-12">Page {page} of {pages}</span>
        <div className="flex gap-8">
          <button className="btn btn-ghost btn-xs" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Previous</button>
          <button className="btn btn-ghost btn-xs" disabled={page >= pages || loading} onClick={() => setPage((current) => current + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}

function KycDocumentViewer({ document, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [asset, setAsset] = useState(null);
  const review = document?.review || {};

  useEffect(() => {
    let objectUrl = null;
    api.get("/media/" + document.id, { responseType: "blob" })
      .then((response) => {
        objectUrl = URL.createObjectURL(response.data);
        setAsset({ url: objectUrl, mime: response.headers["content-type"] || response.data.type || "" });
      })
      .catch(() => setError("This document could not be loaded. Please refresh and try again."))
      .finally(() => setLoading(false));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [document.id]);

  const isPdf = asset?.mime.toLowerCase().includes("pdf");
  const renderQuestionnaire = (items = []) => items.length ? items.map((item, idx) => (
    <div key={`${item.question}-${idx}`} style={{ marginBottom: "var(--space-3)" }}>
      <div className="text-muted fs-11 mb-4">{item.question}</div>
      <div className="fs-13">
        {Array.isArray(item.answer) ? item.answer.join(", ") : typeof item.answer === "object" ? JSON.stringify(item.answer) : String(item.answer || "—")}
      </div>
    </div>
  )) : <div className="text-muted fs-12">No questionnaire answers available.</div>;

  return (
    <div className="modal-bg" onClick={onClose} data-testid="kyc-document-viewer">
      <div className="modal-card" style={{ width: "min(1100px, 96vw)", maxHeight: "92vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-16">
          <div>
            <div className="font-serif fs-20 fw-700">{document.field.toUpperCase()} document</div>
            {document.artist.trim() && <div className="text-muted fs-12">{document.artist.trim()}</div>}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>

        <div className="grid grid-2 gap-16">
          <div>
            {loading && <div className="empty"><div className="empty-title">Loading document…</div></div>}
            {error && <div className="empty"><div className="empty-title">{error}</div></div>}
            {asset && (isPdf ? (
              <iframe title={document.field + " document"} src={asset.url} style={{ width: "100%", height: "68vh", border: 0, borderRadius: 8, background: "#fff" }} />
            ) : (
              <img src={asset.url} alt={document.field} style={{ display: "block", maxWidth: "100%", maxHeight: "68vh", margin: "0 auto", borderRadius: 8 }} />
            ))}
            {asset && (
              <a className="btn btn-ghost btn-sm mt-12" href={asset.url} download={document.field + "-kyc-document"} data-testid="kyc-document-download">
                Download document
              </a>
            )}
          </div>

          <div className="card card-pad" style={{ background: "rgba(255,255,255,0.03)" }}>
            <div className="fw-700 mb-12">Artist review details</div>
            <div className="mb-12">
              <div className="text-muted fs-11 mb-4">Identity</div>
              <div className="fs-13"><strong>Name:</strong> {review.name || "—"}</div>
              <div className="fs-13"><strong>Stage name:</strong> {review.stage_name || "—"}</div>
              <div className="fs-13"><strong>Category:</strong> {review.category || "—"}</div>
              <div className="fs-13"><strong>City:</strong> {review.city || "—"}</div>
              <div className="fs-13"><strong>Email:</strong> {review.email || "—"}</div>
            </div>

            <div className="mb-12">
              <div className="text-muted fs-11 mb-4">KYC fields</div>
              <div className="fs-13"><strong>DOB:</strong> {review.dob || "—"}</div>
              <div className="fs-13"><strong>PAN:</strong> {review.pan_number || "—"}</div>
              <div className="fs-13"><strong>Aadhaar:</strong> {review.aadhaar_number_masked || "—"}</div>
            </div>

            <div className="mb-12">
              <div className="text-muted fs-11 mb-4">Decision</div>
              <div className="fs-13"><strong>Status:</strong> {review.status || "—"}</div>
              <div className="fs-13"><strong>Submitted:</strong> {review.submitted_at ? new Date(review.submitted_at).toLocaleString() : "—"}</div>
              <div className="fs-13"><strong>Decision by:</strong> {review.decided_by || "—"}</div>
              <div className="fs-13"><strong>Reason:</strong> {review.reason || "—"}</div>
            </div>

            {review.about && (
              <div>
                <div className="fw-600 mb-8">Questionnaire</div>
                {(["universal", "category"]).map((key) => {
                  const items = review.about?.[key] || [];
                  if (!items.length) return null;
                  return (
                    <div key={key} style={{ marginBottom: "var(--space-3)" }}>
                      <div className="text-muted fs-11 mb-4">{key === "universal" ? "Profile details" : "Category details"}</div>
                      {renderQuestionnaire(items)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// KycApproveModal — captures artist_type ("normal" / "service") and, if
// service, the percentage_deal. Submitted as part of /admin/kyc/decide
// so the artist's commercial terms are locked at approval time.
// ────────────────────────────────────────────────────────────────────────
function KycApproveModal({ artist, onCancel, onConfirm, onNavigateToRequest }) {
  const [type, setType] = useState("normal");
  const [pct, setPct] = useState(15);
  const [busy, setBusy] = useState(false);
  const pendingCategory = artist.artist_profile?.category_pending;
  const pendingCity = artist.artist_profile?.city_pending;
  const hasPendingRequests = pendingCategory || pendingCity;

  const submit = async () => {
    if (type === "service" && (!pct || pct <= 0 || pct > 50)) {
      alert("Enter a BookTalent commission percentage between 1 and 50.");
      return;
    }
    setBusy(true);
    await onConfirm({
      artist_type: type,
      percentage_deal: type === "service" ? Number(pct) : 0,
    });
    setBusy(false);
  };

  return (
    <div
      data-testid="kyc-approve-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(36,34,31,0.36)",
        display: "grid", placeItems: "center", zIndex: 900, padding: "var(--space-4)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onCancel}>
      <div className="card card-pad"
        style={{ maxWidth: 480, width: "100%", background: "var(--bg1)", color: "var(--white)" }}
        onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif fw-700 fs-18 mb-4">Approve KYC · {artist.name}</h3>
        <p className="text-muted fs-12 mb-12">
          Set the commercial deal now. This is what the customer sees on every future checkout.
        </p>

        {hasPendingRequests && (
          <div
            className="mb-12"
            style={{ padding: "var(--space-3)", borderRadius: 10, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)" }}
            data-testid="kyc-pending-profile-values"
          >
            <div className="fw-700 fs-13 mb-4">Review new profile values before KYC approval</div>
            <div className="text-muted fs-12 mb-8">
              These values are not searchable until their requests are approved. Resolve each request, then reopen this approval.
            </div>
            <div className="flex gap-8" style={{ flexWrap: "wrap" }}>
              {pendingCategory && (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => onNavigateToRequest("category-requests", artist.artist_profile.pending_category_id)}
                  data-testid="kyc-review-pending-category"
                >
                  Review category: {artist.artist_profile.pending_category_name || artist.artist_profile.category || "pending"}
                </button>
              )}
              {pendingCity && (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => onNavigateToRequest("city-requests", artist.artist_profile.pending_city_id)}
                  data-testid="kyc-review-pending-city"
                >
                  Review city: {artist.artist_profile.pending_city_name || artist.artist_profile.city || "pending"}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="field-label mb-4">Artist type</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
          <label
            data-testid="kyc-type-normal"
            onClick={() => setType("normal")}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              width: "100%",
              minWidth: 0,
              minHeight: 148,
              padding: "var(--space-3)",
              borderRadius: 12,
              cursor: "pointer",
              border: type === "normal" ? "1.5px solid var(--gold)" : "1.5px solid var(--glass-border)",
              background: type === "normal" ? "var(--gold-dim)" : "var(--bg3)",
              boxSizing: "border-box",
            }}>
            <div className="fw-700 fs-13">Normal Artist</div>
            <div className="text-muted fs-11" style={{ lineHeight: 1.5 }}>
              Customer pays a 5% Platform Fee. No commission from artist.
            </div>
          </label>
          <label
            data-testid="kyc-type-service"
            onClick={() => setType("service")}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              width: "100%",
              minWidth: 0,
              minHeight: 148,
              padding: "var(--space-3)",
              borderRadius: 12,
              cursor: "pointer",
              border: type === "service" ? "1.5px solid var(--green)" : "1.5px solid var(--glass-border)",
              background: type === "service" ? "var(--green-dim)" : "var(--bg3)",
              boxSizing: "border-box",
            }}>
            <div className="fw-700 fs-13">BookTalent Service Artist</div>
            <div className="text-muted fs-11" style={{ lineHeight: 1.5 }}>
              Customer's 5% Platform Fee waived. Artist gives BookTalent a % commission.
            </div>
          </label>
        </div>

        {type === "service" && (
          <div className="mb-12" data-testid="kyc-service-pct-wrap">
            <div className="field-label mb-4">BookTalent Commission (%)</div>
            <input
              type="number"
              min={1}
              max={50}
              step={0.5}
              className="input"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              data-testid="kyc-service-pct"
              placeholder="15"
              style={{ width: "100%", boxSizing: "border-box" }}
            />
            <div className="text-muted fs-11 mt-4" style={{ lineHeight: 1.5 }}>
              e.g. 15 → on a ₹1,00,000 booking, BookTalent = ₹15,000 · Artist = ₹85,000.
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} data-testid="kyc-approve-cancel">Cancel</button>
          <button className="btn btn-green btn-sm" onClick={submit} disabled={busy || hasPendingRequests} data-testid="kyc-approve-confirm">
            {hasPendingRequests ? "Resolve city/category requests first" : busy ? "Approving…" : "✓ Approve & Set Deal"}
          </button>
        </div>
      </div>
    </div>
  );
}


function AdminRefunds({ toast }) {
  const [list, setList] = useState({ items: [] });
  const [busy, setBusy] = useState(null);
  const reload = () => api.get("/admin/refunds")
    .then(r => setList(r.data || { items: [] }))
    .catch(() => setList({ items: [] }));
  useEffect(() => { reload(); }, []);
  // Iter 65 — support ?highlight=<payment_id> from refund notifications.
  const items = list.items || [];
  useHighlightRow({ prefix: "refund-row", dataKey: items.length });
  const retryRefund = async (paymentId) => {
    if (!window.confirm("Retry Easebuzz refund for this payment?")) return;
    setBusy(paymentId);
    try {
      const r = await api.post(`/admin/refunds/${paymentId}/retry`);
      toast(r.data?.ok ? "Refund succeeded" : (r.data?.reason || "Attempt made"),
            r.data?.ok ? "success" : "warn");
      reload();
    } catch (e) { toast(e?.response?.data?.detail || "Refund retry failed", "error"); }
    setBusy(null);
  };
  return (
    <div className="card" data-testid="admin-refunds">
      <div className="card-head">
        <div className="card-title"> Automatic Refunds ({items.length})</div>
        <div className="text-muted fs-12">Refunds are triggered automatically via Easebuzz when bookings are rejected, cancelled or expire. No manual processing needed.</div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>When</th><th>Customer</th><th>Artist</th><th>Amount</th><th>Reason</th><th>Status</th><th>Ref</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="empty">No refunds yet</td></tr>}
            {items.map((w) => (
              <tr key={w.payment_id} data-testid={`refund-row-${w.payment_id}`}>
                <td className="text-muted fs-12">{(w.refund_at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{w.customer_name || "—"}<div className="text-muted fs-11">{w.customer_email || ""}</div></td>
                <td className="fs-12">{w.artist_name || "—"}</td>
                <td className="text-gold font-serif fs-14 fw-700">{fmtMoneyNumber(w.refund_amount || 0)}</td>
                <td className="fs-11">{w.refund_reason || "—"}</td>
                <td className="fs-12" style={{ textTransform: "capitalize" }}>{(w.refund_status || "—").replace(/_/g, " ")}</td>
                <td><code className="fs-11">{w.refund_id || w.easebuzz_id || "—"}</code></td>
                <td>
                  {w.refund_status === "failed" && (
                    <button className="btn btn-green btn-xs" disabled={busy === w.payment_id}
                      onClick={() => retryRefund(w.payment_id)}
                      data-testid={`process-refund-${w.payment_id}`}>
                      {busy === w.payment_id ? "Processing…" : "Retry"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminCoupons({ toast }) {
  const [analytics, setAnalytics] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [drillIn, setDrillIn] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [form, setForm] = useState({ code: "", description: "", discount_type: "percent", discount_value: 10, max_uses: 100, per_user_limit: 1, expires_at: "2026-12-31", min_order: 0, applies_to: "all", active: true });
  const reload = () => api.get("/admin/coupons/analytics").then(r => setAnalytics(r.data)).catch(() => setAnalytics({}));
  useEffect(() => { reload(); }, []);
  const create = async () => {
    try { await api.post("/admin/coupons", form); toast("Created"); setShowAdd(false); reload(); }
    catch (e) { toast(formatApiError(e), "error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete coupon?")) return; await api.delete(`/admin/coupons/${id}`); reload(); };
  const drill = async (c) => {
    setDrillIn(c);
    const r = await api.get(`/admin/coupons/${c.id}/redemptions`);
    setLedger(r.data);
  };

  return (
    <div className="card" data-testid="admin-coupons">
      <div className="card-head">
        <div className="card-title"> Coupons & Analytics ({analytics.length})</div>
        <button className="btn btn-gold btn-sm" onClick={() => setShowAdd(true)} data-testid="add-coupon-btn">+ New Coupon</button>
      </div>

      <div className="kpi-grid" style={{ padding: "var(--space-3) 14px 0" }}>
        <div className="kpi" data-testid="coupon-kpi-uses"><div className="kpi-num">{analytics.reduce((s, c) => s + c.uses, 0)}</div><div className="kpi-label">Total Redemptions</div></div>
        <div className="kpi" data-testid="coupon-kpi-discount"><div className="kpi-num text-gold">{fmtINRFull(analytics.reduce((s, c) => s + c.total_discount, 0))}</div><div className="kpi-label">Total Discount Given</div></div>
        <div className="kpi" data-testid="coupon-kpi-gmv"><div className="kpi-num text-gold">{fmtINRFull(analytics.reduce((s, c) => s + c.total_gmv, 0))}</div><div className="kpi-label">Coupon-Driven GMV</div></div>
        <div className="kpi" data-testid="coupon-kpi-active"><div className="kpi-num">{analytics.filter(c => c.active).length}</div><div className="kpi-label">Active Coupons</div></div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Code</th><th>Discount</th><th>Uses</th><th>Discount Given</th><th>GMV Driven</th><th>Expires</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {analytics.map((c) => (
              <tr key={c.id} data-testid={`coupon-${c.code}`}>
                <td><code style={{ color: "var(--gold-light)", background: "var(--gold-dim)", padding: "var(--space-1) 8px", borderRadius: 5 }}>{c.code}</code></td>
                <td>{c.discount_type === "percent" ? `${c.discount_value}%` : fmtMoneyNumber(c.discount_value)}</td>
                <td>{c.uses} / {c.max_uses}</td>
                <td className="text-gold">{fmtMoneyNumber(c.total_discount)}</td>
                <td className="text-gold">{fmtMoneyNumber(c.total_gmv)}</td>
                <td className="fs-12 text-muted">{c.expires_at}</td>
                <td><span className={`pill ${c.active ? "pill-green" : "pill-red"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                <td>
                  <button className="btn btn-ghost btn-xs" onClick={() => drill(c)} data-testid={`drill-coupon-${c.id}`}>Ledger</button>
                  <button className="btn btn-red btn-xs" onClick={() => del(c.id)} style={{ marginLeft: 6 }} data-testid={`del-coupon-${c.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="modal-bg" onClick={() => setShowAdd(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">New Coupon</div>
            <div className="field"><div className="field-label">Code</div><input className="field-input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} data-testid="coupon-code" /></div>
            <div className="field"><div className="field-label">Description</div><input className="field-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div className="field-row">
              <div className="field"><div className="field-label">Type</div>
                <select className="field-input" value={form.discount_type} onChange={(e) => setForm({...form, discount_type: e.target.value})}>
                  <option value="percent">Percent</option><option value="flat">Flat ₹</option>
                </select>
              </div>
              <div className="field"><div className="field-label">Value</div><input type="number" min="0" className="field-input" value={form.discount_value} onChange={(e) => setForm({...form, discount_value: Number(e.target.value)})} data-testid="coupon-value" /></div>
            </div>
            <div className="field-row">
              <div className="field"><div className="field-label">Max Uses (total)</div><input type="number" min="0" className="field-input" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: Number(e.target.value)})} /></div>
              <div className="field"><div className="field-label">Per User Limit</div><input type="number" min="1" className="field-input" value={form.per_user_limit} onChange={(e) => setForm({...form, per_user_limit: Number(e.target.value)})} /></div>
            </div>
            <div className="field-row">
              <div className="field"><div className="field-label">Min Order ₹</div><input type="number" min="0" className="field-input" value={form.min_order} onChange={(e) => setForm({...form, min_order: Number(e.target.value)})} /></div>
              <div className="field"><div className="field-label">Expires</div><input type="date" className="field-input" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} /></div>
            </div>
            <div className="field"><div className="field-label">Applies To</div>
              <select className="field-input" value={form.applies_to} onChange={(e) => setForm({...form, applies_to: e.target.value})}>
                <option value="all">All bookings</option>
                <option value="wedding">Weddings only</option>
                <option value="corporate">Corporate only</option>
                <option value="birthday">Birthdays only</option>
              </select>
            </div>
            <div className="flex gap-12">
              <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-gold" style={{ flex: 1 }} onClick={create} data-testid="save-coupon">Create</button>
            </div>
          </div>
        </div>
      )}

      {drillIn && (
        <div className="modal-bg" onClick={() => setDrillIn(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-title">Redemption Ledger — <code style={{ color: "var(--gold)" }}>{drillIn.code}</code></div>
            <p className="text-muted fs-13 mb-12">{ledger.length} redemptions · ₹{drillIn.total_discount.toLocaleString("en-IN")} discount given</p>
            <div className="table-wrap" style={{ maxHeight: 400, overflow: "auto" }}>
              <table className="table">
                <thead><tr><th>When</th><th>User</th><th>Booking</th><th>Discount</th><th>Total</th></tr></thead>
                <tbody>
                  {ledger.map((r) => (
                    <tr key={r.id} data-testid={`ledger-${r.id}`}>
                      <td className="fs-11 text-muted">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td>{r.user?.email || r.user_id?.slice(0, 8)}</td>
                      <td><code className="fs-11">{r.booking?.ref || r.booking_id?.slice(0, 8)}</code></td>
                      <td className="text-gold">{fmtMoneyNumber(r.discount_amount)}</td>
                      <td>{fmtMoneyNumber(r.booking_total || 0)}</td>
                    </tr>
                  ))}
                  {ledger.length === 0 && <tr><td colSpan={5} className="text-muted" style={{ textAlign: "center", padding: "var(--space-4)" }}>No redemptions yet</td></tr>}
                </tbody>
              </table>
            </div>
            <button className="btn btn-ghost mt-12" onClick={() => setDrillIn(null)} style={{ marginTop: "var(--space-3)" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminUsers({ toast }) {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [scrollTop, setScrollTop] = useState(0);
  const tableRef = useRef(null);
  const [filter, setFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [activityFor, setActivityFor] = useState(null);
  const loadSequence = useRef(0);
  const reload = useCallback(() => {
    const requestId = ++loadSequence.current;
    const params = new URLSearchParams();
    if (filter) params.set("role", filter);
    if (includeDeleted) params.set("include_deleted", "true");
    params.set("page", String(page));
    params.set("limit", String(pageSize));
    const query = params.toString();
    api.get(`/admin/users?${query}`).then((r) => {
      if (requestId !== loadSequence.current) return;
      if (r.data.page !== page) setPage(r.data.page);
      setList(r.data.items);
      setTotal(r.data.total);
      setPages(Math.max(1, r.data.pages));
    }).catch((error) => {
      if (requestId !== loadSequence.current) return;
      toast?.(formatApiError(error), "error");
      setList([]);
      setTotal(0);
      setPages(1);
    });
  }, [filter, includeDeleted, page, pageSize, toast]);
  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollTop = 0;
    setScrollTop(0);
    reload();
  }, [reload]);
  const suspend = async (uid) => {
    const r = await api.post(`/admin/artists/${uid}/suspend`);
    toast?.(r.data.suspended ? "Suspended" : "Unsuspended");
    reload();
  };
  const virtualRows = getVirtualWindow(scrollTop, 80, 560, list.length);
  const visibleUsers = list.slice(virtualRows.start, virtualRows.end);

  return (
    <div className="card" data-testid="admin-users">
      <div className="card-head">
        <div className="card-title"> Users ({total.toLocaleString("en-IN")})</div>
        <div className="flex items-center gap-12">
          <label className="flex items-center gap-8 fs-12 text-muted">
            <input type="checkbox" checked={includeDeleted} onChange={(event) => { setIncludeDeleted(event.target.checked); setPage(1); }} data-testid="users-include-deactivated" />
            Show deactivated
          </label>
          <select className="field-input" style={{ maxWidth: 200 }} value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }} data-testid="user-role-filter">
            <option value="">All Roles</option>
            <option value="customer">Customers</option>
            <option value="artist">Artists</option>
            <option value="agency">Agencies</option>
            <option value="corporate">Corporate</option>
          </select>
        </div>
      </div>
      <div
        className="table-wrap"
        ref={tableRef}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        style={{ maxHeight: 560, overflowY: "auto" }}
      >
        <table className="table admin-users-virtual-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Phone</th><th>Joined</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {virtualRows.topPadding > 0 && <tr className="admin-virtual-spacer" aria-hidden="true"><td colSpan={7} style={{ height: virtualRows.topPadding, "--virtual-spacer-height": `${virtualRows.topPadding}px` }} /></tr>}
            {visibleUsers.map((u) => (
              <tr key={u.id} data-testid={`user-${u.id}`}>
                <td>{u.first_name} {u.last_name}</td>
                <td className="text-muted">
                  {u.deleted ? (u.deactivated_email || u.email) : u.email}
                  {u.deleted && !u.deactivated_email && <div className="text-amber fs-11">Original email not saved</div>}
                </td>
                <td><span className="pill pill-purple">{u.role}</span></td>
                <td>{u.phone || "—"}</td>
                <td className="fs-12 text-muted">{u.created_at?.slice(0, 10)}</td>
                <td>
                  {u.deleted ? <span className="pill pill-red">Deactivated</span> : u.suspended ? <span className="pill pill-red">Suspended</span> : <span className="pill pill-green">Active</span>}
                </td>
                <td>
                  <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
                    {u.deleted ? (
                      <button className="btn btn-green btn-xs" onClick={() => setRestoring(u)} data-testid={`user-restore-${u.id}`}>
                        Reactivate
                      </button>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-xs" onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`}>Edit</button>
                        <button className={`btn btn-xs ${u.suspended ? "btn-green" : "btn-amber"}`} onClick={() => suspend(u.id)} data-testid={`user-suspend-${u.id}`}>
                          {u.suspended ? "Unsuspend" : "Suspend"}
                        </button>
                      </>
                    )}
                    <button className="btn btn-ghost btn-xs" onClick={() => setActivityFor(u)} data-testid={`user-activity-${u.id}`}>Activity</button>
                    <button className="btn btn-red btn-xs" onClick={() => setDeleting({ id: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email })} data-testid={`user-delete-${u.id}`}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {virtualRows.bottomPadding > 0 && <tr className="admin-virtual-spacer" aria-hidden="true"><td colSpan={7} style={{ height: virtualRows.bottomPadding, "--virtual-spacer-height": `${virtualRows.bottomPadding}px` }} /></tr>}
          </tbody>
        </table>
      </div>
      <AdminListPagination
        page={page}
        pages={pages}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        testId="users"
        itemLabel="users"
      />
      {editing && (
        <UserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); toast?.("Saved"); }}
          toast={toast}
        />
      )}
      {deleting && (
        <UserDeleteModal
          target={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); reload(); }}
          toast={toast}
        />
      )}
      {restoring && (
        <UserRestoreModal
          target={restoring}
          onClose={() => setRestoring(null)}
          onDone={() => { setRestoring(null); reload(); }}
          toast={toast}
        />
      )}
      <UserActivityDrawer user={activityFor} onClose={() => setActivityFor(null)} />
    </div>
  );
}

function UserDeleteModal({ target, onClose, onDone, toast }) {
  const [mode, setMode] = useState("soft");   // "soft" | "hard"
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const needConfirm = mode === "hard";
  const canDelete = !needConfirm || confirmText === "DELETE";
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${target.id}${mode === "hard" ? "?hard=true" : ""}`);
      toast?.(mode === "hard" ? "Permanently deleted" : "Deactivated");
      onDone();
    } catch (e) {
      toast?.(e?.response?.data?.detail || "Delete failed", "error");
      setBusy(false);
    }
  };
  return (
    <div className="popup-scrim user-delete-scrim" onClick={onClose} data-testid="user-delete-modal">
      <div
        className="popup-card user-delete-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-delete-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="popup-close" onClick={onClose} aria-label="Close">×</button>
        <div className="user-delete-heading">
          <span className="user-delete-icon" aria-hidden="true"><Trash2 size={19} /></span>
          <h3 id="user-delete-title">Delete {target.label}</h3>
        </div>
        <p className="user-delete-description">
          Pick how you want to remove this account. Deactivation is fully reversible; permanent deletion is not.
        </p>

        <label
          className={`delete-mode-card user-delete-option ${mode === "soft" ? "is-selected is-reversible" : ""}`}
          data-testid="delete-mode-soft"
        >
          <input type="radio" name="user-delete-mode" checked={mode === "soft"} onChange={() => setMode("soft")} />
          <span className="user-delete-option-content">
            <span className="user-delete-option-title">Deactivate <span className="user-delete-recommended">Recommended</span></span>
            <span className="user-delete-option-description">
              Suspends login and anonymises the email. Bookings and financial history stay intact. Reversible.
            </span>
          </span>
        </label>

        <label
          className={`delete-mode-card user-delete-option user-delete-option-danger ${mode === "hard" ? "is-selected" : ""}`}
          data-testid="delete-mode-hard"
        >
          <input type="radio" name="user-delete-mode" checked={mode === "hard"} onChange={() => setMode("hard")} />
          <span className="user-delete-option-content">
            <span className="user-delete-option-title">Delete permanently</span>
            <span className="user-delete-option-description">
              Removes the account, profile, packages, media and reviews. Booking records remain for financial history. Cannot be undone.
            </span>
          </span>
        </label>

        {needConfirm && (
          <div className="user-delete-confirmation">
            <label className="field-label" htmlFor="delete-confirm-input">Type <b>DELETE</b> to confirm permanent removal</label>
            <input
              id="delete-confirm-input"
              className="field-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
              placeholder="DELETE"
              autoComplete="off"
            />
          </div>
        )}

        <div className="user-delete-actions">
          <button className="btn btn-ghost" onClick={onClose} data-testid="delete-cancel">Cancel</button>
          <button
            className="btn btn-red"
            onClick={submit}
            disabled={busy || !canDelete}
            data-testid="delete-confirm"
          >
            {busy ? "Working…" : mode === "hard" ? "Delete permanently" : "Deactivate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserRestoreModal({ target, onClose, onDone, toast }) {
  const [email, setEmail] = useState(target.deactivated_email || "");
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.id}/restore`, { email: email.trim() });
      toast?.("Account restored with its login email", "success");
      onDone();
    } catch (error) {
      toast?.(error?.response?.data?.detail || "Account restore failed", "error");
      setBusy(false);
    }
  };

  return (
    <div className="popup-scrim" onClick={onClose} data-testid="user-restore-modal">
      <div className="popup-card" style={{ maxWidth: 500 }} onClick={(event) => event.stopPropagation()}>
        <button className="popup-close" onClick={onClose} aria-label="Close">×</button>
        <h3 style={{ marginTop: 0 }}>Restore {target.stage_name || target.first_name || target.label || "account"}</h3>
        <p className="text-muted fs-13">
          Restoring clears the deactivation and artist suspension flags. The account must still meet its normal verification requirements before it can be booked.
        </p>
        {!target.deactivated_email && (
          <p className="text-amber fs-12">This older deactivation did not save the original email. Enter the account’s original login email to restore it.</p>
        )}
        <div className="field">
          <div className="field-label">Login email</div>
          <input type="email" className="field-input" value={email} onChange={(event) => setEmail(event.target.value)} data-testid="restore-user-email" />
        </div>
        <div className="flex gap-8" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={restore} disabled={busy || !email.trim()} data-testid="restore-user-confirm">
            {busy ? "Restoring…" : "Restore account"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserEditModal({ user, profile, onClose, onSaved, toast }) {
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    role: user?.role || "customer",
    stage_name: profile?.stage_name || "",
    category: profile?.category || "",
    city: profile?.city || "",
    starting_price: profile?.starting_price || 0,
    bio: profile?.bio || "",
  });
  const [saving, setSaving] = useState(false);
  const isArtist = form.role === "artist";
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!isArtist) {
        delete body.stage_name; delete body.category; delete body.city;
        delete body.starting_price; delete body.bio;
      }
      await api.put(`/admin/users/${user.id}`, body);
      onSaved();
    } catch (error) {
      toast?.(error?.response?.data?.detail || "Save failed", "error");
      setSaving(false);
    }
  };
  return (
    <div className="popup-scrim" onClick={onClose} data-testid="user-edit-modal">
      <div className="popup-card user-edit-dialog" style={{ maxWidth: 560 }} onClick={(event) => event.stopPropagation()}>
        <button className="popup-close" onClick={onClose} aria-label="Close">×</button>
        <h3 style={{ marginTop: 0 }}>Edit User</h3>
        <div className="grid grid-2 gap-12">
          <div>
            <div className="field-label">First name</div>
            <input className="field-input" value={form.first_name} onChange={(event) => setForm({ ...form, first_name: event.target.value })} data-testid="edit-first-name" />
          </div>
          <div>
            <div className="field-label">Last name</div>
            <input className="field-input" value={form.last_name} onChange={(event) => setForm({ ...form, last_name: event.target.value })} data-testid="edit-last-name" />
          </div>
        </div>
        <div className="field">
          <div className="field-label">Email</div>
          <input className="field-input" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} data-testid="edit-email" />
        </div>
        <div className="grid grid-2 gap-12">
          <div>
            <div className="field-label">Phone</div>
            <input className="field-input" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} data-testid="edit-phone" />
          </div>
          <div>
            <div className="field-label">Role</div>
            <select className="field-input" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} data-testid="edit-role">
              {["customer", "artist", "agency", "corporate", "admin"].map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
          </div>
        </div>
        {isArtist && (
          <>
            <div className="field-label" style={{ marginTop: "var(--space-3)", opacity: 0.6, fontSize: 11 }}>ARTIST PROFILE</div>
            <div className="grid grid-2 gap-12">
              <div>
                <div className="field-label">Stage name</div>
                <input className="field-input" value={form.stage_name} onChange={(event) => setForm({ ...form, stage_name: event.target.value })} data-testid="edit-stage-name" />
              </div>
              <div>
                <div className="field-label">Category</div>
                <input className="field-input" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} data-testid="edit-category" />
              </div>
            </div>
            <div className="grid grid-2 gap-12">
              <div>
                <div className="field-label">City</div>
                <input className="field-input" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} data-testid="edit-city" />
              </div>
              <div>
                <div className="field-label">Starting price (₹)</div>
                <input type="number" min="0" className="field-input" value={form.starting_price} onChange={(event) => setForm({ ...form, starting_price: parseFloat(event.target.value) || 0 })} data-testid="edit-starting-price" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">Bio</div>
              <textarea className="field-input" rows={3} value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} data-testid="edit-bio" />
            </div>
          </>
        )}
        <div className="flex gap-8" style={{ marginTop: "var(--space-4)", justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose} data-testid="edit-cancel">Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminDisputes({ toast }) {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const loadSequence = useRef(0);
  const load = () => {
    const requestId = ++loadSequence.current;
    return api.get(`/admin/disputes?page=${page}&limit=${pageSize}`).then((response) => {
    if (requestId !== loadSequence.current) return;
    if (response.data.page !== page) setPage(response.data.page);
    setList(response.data.items);
    setTotal(response.data.total);
    setPages(Math.max(1, response.data.pages));
    }).catch((error) => {
    if (requestId !== loadSequence.current) return;
    toast?.(formatApiError(error), "error");
    setList([]);
    setTotal(0);
    setPages(1);
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, pageSize]);
  const resolve = async (did, decision) => {
    try {
      await api.post(`/admin/disputes/${did}/resolve`, { decision });
      toast("Resolved");
      load();
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };
  return (
    <div className="card" data-testid="admin-disputes">
      <div className="card-head"><div className="card-title"> Disputes ({total.toLocaleString("en-IN")})</div></div>
      <div style={{ padding: "var(--space-3)" }}>
        {list.length === 0 && <div className="empty"><div className="empty-icon"></div><div className="empty-title">No disputes</div></div>}
        <VirtualizedList
          items={list}
          getKey={(item) => item.id}
          estimatedItemHeight={112}
          testId="disputes-virtual-list"
          resetKey={page}
          renderItem={(d) => (
          <div className="card admin-virtual-item-card" data-testid={`dispute-${d.id}`}>
            <div className="fw-600 mb-4">{d.reason}</div>
            <div className="text-muted fs-12 mb-8">{d.description}</div>
            <div className="flex gap-8">
              <button className="btn btn-green btn-xs" onClick={() => resolve(d.id, "release")}>Release to Artist</button>
              <button className="btn btn-red btn-xs" onClick={() => resolve(d.id, "refund")}>Refund Customer</button>
            </div>
          </div>
          )}
        />
      </div>
      <AdminListPagination
        page={page}
        pages={pages}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        testId="disputes"
        itemLabel="disputes"
      />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Refund Auditor — lists every mutual-refund row with filters + one-click
// CSV / PDF export for finance / audit.
// ────────────────────────────────────────────────────────────────────────
function AdminRefundAuditor({ toast }) {
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState("");
  const [watches, setWatches] = useState([]);
  const watchIds = new Set(watches.map((w) => w.booking_id));

  const loadWatches = () => api.get("/admin/refunds/watchlist")
    .then((r) => setWatches(r.data?.items || []))
    .catch(() => setWatches([]));
  useEffect(() => { loadWatches(); }, []);

  const addWatch = async (bid) => {
    try {
      await api.post("/admin/refunds/watchlist", { booking_id: bid, note: "" });
      loadWatches();
      toast("Added to watchlist — Slack will ping on any refund activity", "success");
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  const removeWatch = async (bid) => {
    const w = watches.find((x) => x.booking_id === bid);
    if (!w) return;
    try {
      await api.delete(`/admin/refunds/watchlist/${w.id}`);
      loadWatches();
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  const qs = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    if (q) p.set("q", q);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/refunds/audit?${qs()}`);
      setItems(r.data?.items || []);
      setTotals(r.data?.totals || {});
    } catch (e) { toast(formatApiError(e), "error"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadViews = () =>
    api.get("/admin/refunds/saved-views").then((r) => setViews(r.data?.items || [])).catch(() => setViews([]));
  useEffect(() => { loadViews(); }, []);

  const saveView = async () => {
    if (!viewName.trim()) { toast("Give the view a name first", "error"); return; }
    try {
      const r = await api.post("/admin/refunds/saved-views", {
        name: viewName.trim(),
        filters: { status, from_date: from, to_date: to, q },
      });
      setViews((v) => [r.data.view, ...v]);
      setViewName("");
      toast("View saved ✓", "success");
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  const applyView = (v) => {
    const f = v.filters || {};
    setStatus(f.status || "");
    setFrom(f.from_date || "");
    setTo(f.to_date || "");
    setQ(f.q || "");
    // trigger load with the new filter values
    setTimeout(load, 20);
  };

  const removeView = async (id) => {
    if (!window.confirm("Delete this saved view?")) return;
    try {
      await api.delete(`/admin/refunds/saved-views/${id}`);
      setViews((v) => v.filter((x) => x.id !== id));
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  const dl = (kind) => {
    const url = `${api.defaults.baseURL}/admin/refunds/audit/export.${kind}?${qs()}`;
    const token = localStorage.getItem("token") || "";
    // Use fetch to attach the Authorization header and then trigger a download.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `refund-audit.${kind}`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => toast(`Export failed: ${e.message}`, "error"));
  };

  return (
    <div className="card" data-testid="admin-refund-auditor">
      <div className="card-head">
        <div className="card-title"> Refund Auditor ({items.length})</div>
        <div className="text-muted fs-12">Every customer/artist mutual-refund request. Filter, then export CSV/PDF for finance.</div>
      </div>

      {/* Totals summary */}
      <div className="kpi-grid" style={{ padding: "var(--space-2) 14px" }}>
        <Kpi icon="" cls="kpi-icon-blue" num={totals.count || 0} label="Total Rows" />
        <Kpi icon="" cls="kpi-icon-amber" num={fmtINRFull(totals.amount_pending || 0)} label="₹ Pending" />
        <Kpi icon="" cls="kpi-icon-green" num={fmtINRFull(totals.amount_accepted || 0)} label="₹ Accepted" />
        <Kpi icon="" cls="kpi-icon-red" num={fmtINRFull(totals.amount_rejected || 0)} label="₹ Rejected" />
      </div>

      {/* Saved views */}
      <div style={{ padding: "0 14px var(--space-2)" }} data-testid="rf-saved-views">
        <div className="flex-between mb-4" style={{ flexWrap: "wrap", gap: "var(--space-2)" }}>
          <div className="text-muted fs-11">
            Saved views — one-click filter combos for month-end audits
          </div>
          <div style={{ display: "flex", gap: "var(--space-1)" }}>
            <input
              className="input"
              style={{ width: 180 }}
              placeholder="Name current filters…"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              data-testid="rf-view-name"
            />
            <button className="btn btn-ghost btn-sm" onClick={saveView} disabled={!viewName.trim()} data-testid="rf-view-save">
               Save view
            </button>
          </div>
        </div>
        {views.length > 0 && (
          <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
            {views.map((v) => (
              <span key={v.id}
                style={{
                  display: "inline-flex", gap: "var(--space-1)", alignItems: "center",
                  border: "1px solid rgba(212,175,55,0.35)", borderRadius: 999,
                  padding: "var(--space-1) 10px", fontSize: 11, background: "rgba(212,175,55,0.06)",
                }}
                data-testid={`rf-view-${v.id}`}>
                <span style={{ cursor: "pointer" }} onClick={() => applyView(v)}>{v.name}</span>
                <span style={{ cursor: "pointer", color: "#e57373" }} onClick={() => removeView(v.id)}>×</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ padding: "var(--space-2) 14px var(--space-3)", display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "end" }}>
        <div>
          <div className="field-label">Status</div>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="rf-status">
            <option value="">All</option>
            <option value="pending_counter_ack">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div><div className="field-label">From</div><input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rf-from" /></div>
        <div><div className="field-label">To</div><input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="rf-to" /></div>
        <div style={{ flex: 1, minWidth: 200 }}><div className="field-label">Search (ref / email)</div><input className="input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rf-q" /></div>
        <button className="btn btn-gold btn-sm" onClick={load} disabled={loading} data-testid="rf-apply">{loading ? "Loading…" : "Apply"}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => dl("csv")} data-testid="rf-csv"> CSV</button>
        <button className="btn btn-ghost btn-sm" onClick={() => dl("pdf")} data-testid="rf-pdf"> PDF</button>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th>Booking</th><th>Event</th><th>Amount</th><th>Status</th>
            <th>Requested By</th><th>Customer</th><th>Artist</th><th>Created</th><th>Watch</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={9} className="empty">No refund requests match your filters</td></tr>}
          {items.map((h) => {
            const isWatched = watchIds.has(h.booking_id);
            return (
              <tr key={h.id} data-testid={`rf-row-${h.id}`}>
                <td><Link to={`/bookings/${h.booking_id}`}>{h.booking_ref}</Link></td>
                <td>{h.event_date || "—"}</td>
                <td>{fmtMoneyNumber(h.amount)}</td>
                <td><span className={`pill pill-${h.status === "accepted" ? "green" : h.status === "rejected" ? "red" : "gold"}`}>{h.status}</span></td>
                <td>{h.requested_by_role}</td>
                <td title={h.customer_email}>{h.customer_name || h.customer_email}</td>
                <td title={h.artist_email}>{h.artist_name || h.artist_email}</td>
                <td>{(h.created_at || "").slice(0, 10)}</td>
                <td>
                  <button
                    className={`btn btn-sm ${isWatched ? "btn-gold" : "btn-ghost"}`}
                    onClick={() => (isWatched ? removeWatch(h.booking_id) : addWatch(h.booking_id))}
                    title={isWatched ? "Watching — click to unwatch" : "Watch this booking for future refund activity"}
                    data-testid={`rf-watch-${h.booking_id}`}
                    style={{ fontSize: 12 }}>
                    {isWatched ? " Watching" : " Watch"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {watches.length > 0 && (
        <div style={{ padding: "var(--space-2) 14px var(--space-4)" }} data-testid="rf-watchlist-summary">
          <div className="text-muted fs-11 mb-4">Active watchlist ({watches.length}) — Slack pings on any refund activity here</div>
          <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
            {watches.map((w) => (
              <span key={w.id}
                style={{
                  display: "inline-flex", gap: "var(--space-1)", alignItems: "center",
                  border: "1px solid rgba(212,175,55,0.35)", borderRadius: 999,
                  padding: "var(--space-1) 10px", fontSize: 11, background: "rgba(212,175,55,0.06)",
                }}
                data-testid={`rf-watch-chip-${w.id}`}>
                <Link to={`/bookings/${w.booking_id}`} style={{ textDecoration: "none" }}>
                  {w.booking_ref}
                </Link>
                {w.trigger_count > 0 && (
                  <span style={{ fontSize: 10, opacity: 0.7 }} title={`${w.trigger_count} alert(s) fired`}>· {w.trigger_count}×</span>
                )}
                <span style={{ cursor: "pointer", color: "#e57373" }} onClick={() => removeWatch(w.booking_id)}>×</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Bulk Payout Marker — select multiple pending payouts, enter UTRs, mark
// all as paid in a single call.
// ────────────────────────────────────────────────────────────────────────
function AdminBulkPayouts({ toast }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [defaultMethod, setDefaultMethod] = useState("neft");
  const [defaultPaidOn, setDefaultPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);   // { matched, ambiguous, unmatched }
  const [csvBusy, setCsvBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [bankPresets, setBankPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState("");
  const [presetPickerName, setPresetPickerName] = useState("");
  const [mappingEditor, setMappingEditor] = useState(null); // {id, bank_name, mapping, headers}

  const load = async () => {
    try {
      const r = await api.get("/admin/payouts/pending-list?limit=200");
      setRows(r.data?.items || []);
      setSelected({});
    } catch (e) { toast(formatApiError(e), "error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectAll = () => {
    const all = {};
    rows.forEach((r) => { all[r.booking_id] = true; });
    setSelected(all);
  };
  const clearAll = () => setSelected({});

  const updateRow = (bid, patch) => setRows((rs) => rs.map((r) => r.booking_id === bid ? { ...r, ...patch } : r));

  const submit = async () => {
    const chosen = rows.filter((r) => selected[r.booking_id]);
    if (chosen.length === 0) { toast("Select at least one row", "error"); return; }
    const payload = {
      default_method: defaultMethod,
      default_paid_on: defaultPaidOn,
      rows: chosen.map((r) => ({
        booking_id: r.booking_id,
        amount: parseFloat(r.pay_amount || r.outstanding),
        method: r.pay_method || defaultMethod,
        utr: r.pay_utr || "",
        notes: r.pay_notes || "",
        paid_on: r.pay_paid_on || defaultPaidOn,
      })),
    };
    if (!window.confirm(`Mark ${chosen.length} payout(s) as paid — total ₹${payload.rows.reduce((s, r) => s + r.amount, 0).toLocaleString("en-IN")}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/payouts/bulk-mark-paid", payload);
      const failed = (r.data?.results || []).filter((x) => !x.ok);
      if (failed.length) {
        toast(`Marked ${r.data.succeeded}/${r.data.processed}. ${failed.length} failed — check console`, "warning");
        console.warn("bulk payout failures", failed);
      } else {
        toast(` Marked ${r.data.succeeded} payouts paid · ₹${(r.data.total_amount || 0).toLocaleString("en-IN")}`, "success");
      }
      load();
    } catch (e) { toast(formatApiError(e), "error"); }
    setBusy(false);
  };

  const chosenCount = Object.values(selected).filter(Boolean).length;
  const chosenTotal = rows.filter((r) => selected[r.booking_id])
    .reduce((s, r) => s + parseFloat(r.pay_amount || r.outstanding || 0), 0);

  // ── CSV import handlers ──────────────────────────────────────────
  const loadBankPresets = () => api.get("/admin/payouts/bank-presets")
    .then((r) => setBankPresets(r.data?.items || []))
    .catch(() => setBankPresets([]));
  useEffect(() => { loadBankPresets(); }, []);

  const uploadCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const url = activePresetId
      ? `/admin/payouts/batch-preview?preset_id=${encodeURIComponent(activePresetId)}`
      : "/admin/payouts/batch-preview";
    setCsvBusy(true);
    try {
      const r = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvPreview(r.data);
      const withPreset = r.data.preset ? ` · using ${r.data.preset.bank_name}` : "";
      toast(`Parsed ${r.data.parsed_rows} rows · ${r.data.matched.length} auto-matched${withPreset}`, "success");
      if (activePresetId) loadBankPresets(); // refresh usage counter
    } catch (e) { toast(formatApiError(e), "error"); }
    setCsvBusy(false);
  };

  const saveBankPreset = async () => {
    if (!presetPickerName.trim()) { toast("Enter the bank name first", "error"); return; }
    // Extract the header names from the last parsed CSV as a starter mapping.
    // If nothing parsed yet, save an empty mapping the admin can fill later.
    const headers = csvPreview?.parsed_rows ? Object.keys((csvPreview.matched[0]?.raw || csvPreview.unmatched[0]?.raw || {})) : [];
    // Auto-detect columns using simple contains rules so the first import is not a chore.
    const guess = (needles) => headers.filter((h) => needles.some((n) => h.toLowerCase().includes(n)));
    const mapping = {
      amount: guess(["amount", "debit", "credit"]),
      utr: guess(["utr", "ref", "txn", "transaction"]),
      ref_hint: guess(["narration", "description", "remark", "particular"]),
      paid_on: guess(["date"]),
    };
    try {
      const r = await api.post("/admin/payouts/bank-presets", {
        bank_name: presetPickerName.trim(),
        mapping,
      });
      setBankPresets((bs) => [r.data.preset, ...bs]);
      setPresetPickerName("");
      toast(`Saved bank preset "${r.data.preset.bank_name}"`, "success");
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  const deleteBankPreset = async (id) => {
    if (!window.confirm("Delete this bank preset?")) return;
    try {
      await api.delete(`/admin/payouts/bank-presets/${id}`);
      setBankPresets((bs) => bs.filter((b) => b.id !== id));
      if (activePresetId === id) setActivePresetId("");
    } catch (e) { toast(formatApiError(e), "error"); }
  };

  const applyCsvMatched = async () => {
    if (!csvPreview?.matched?.length) return;
    if (!window.confirm(`Mark ${csvPreview.matched.length} auto-matched payouts as paid?`)) return;
    setCsvBusy(true);
    try {
      const payload = {
        default_method: defaultMethod,
        default_paid_on: defaultPaidOn,
        rows: csvPreview.matched.map((m) => ({
          booking_id: m.booking_id,
          amount: m.amount,
          utr: m.utr || "",
          method: defaultMethod,
          paid_on: m.paid_on || defaultPaidOn,
          notes: `CSV batch · ${m.match_reason}`,
        })),
      };
      const r = await api.post("/admin/payouts/batch-apply", payload);
      toast(
        ` Marked ${r.data.succeeded}/${r.data.processed} paid · ₹${(r.data.total_amount || 0).toLocaleString("en-IN")}`,
        r.data.failed ? "warning" : "success",
      );
      setCsvPreview(null);
      load();
    } catch (e) { toast(formatApiError(e), "error"); }
    setCsvBusy(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadCsv(f);
  };

  return (
    <div className="card" data-testid="admin-bulk-payouts">
      <div className="card-head">
        <div className="card-title"> Bulk Payout Marker ({rows.length} pending)</div>
        <div className="text-muted fs-12">
          Pick payouts you've settled through your bank portal, drop in a UTR, and mark them all paid at once.
        </div>
      </div>

      <div style={{ padding: "var(--space-2) 14px var(--space-3)", display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "end" }}>
        <div>
          <div className="field-label">Default Method</div>
          <select className="input" value={defaultMethod} onChange={(e) => setDefaultMethod(e.target.value)} data-testid="bp-method">
            <option value="neft">NEFT</option>
            <option value="imps">IMPS</option>
            <option value="upi">UPI</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <div className="field-label">Default Paid On</div>
          <input type="date" className="input" value={defaultPaidOn} onChange={(e) => setDefaultPaidOn(e.target.value)} data-testid="bp-paidon" />
        </div>
        <button className="btn btn-ghost btn-sm" onClick={selectAll} data-testid="bp-select-all">Select All</button>
        <button className="btn btn-ghost btn-sm" onClick={clearAll} data-testid="bp-clear">Clear</button>
        <div style={{ flex: 1 }} />
        <div className="text-muted fs-12">Selected: <b className="text-good">{chosenCount}</b> · ₹{chosenTotal.toLocaleString("en-IN")}</div>
        <button className="btn btn-gold btn-sm" onClick={submit} disabled={busy || chosenCount === 0} data-testid="bp-submit">
          {busy ? "Processing…" : `Mark ${chosenCount || ""} Paid`}
        </button>
      </div>

      {/* CSV drag-drop importer */}
      <div style={{ padding: "0 14px var(--space-3)" }}>
        {/* Bank preset picker */}
        <div className="flex-between mb-8" style={{ flexWrap: "wrap", gap: "var(--space-2)" }} data-testid="bp-bank-presets">
          <div className="text-muted fs-11">
            Bank preset (column mapping) — pick one to auto-recognise headers
          </div>
          <div style={{ display: "flex", gap: "var(--space-1)", alignItems: "center", flexWrap: "wrap" }}>
            <select
              className="input"
              value={activePresetId}
              onChange={(e) => setActivePresetId(e.target.value)}
              style={{ minWidth: 160 }}
              data-testid="bp-bank-preset-select">
              <option value="">Auto-detect (default)</option>
              {bankPresets.map((b) => (
                <option key={b.id} value={b.id}>{b.bank_name}{b.usage_count ? ` · ${b.usage_count}×` : ""}</option>
              ))}
            </select>
            {activePresetId && (
              <>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const p = bankPresets.find((b) => b.id === activePresetId);
                    if (p) setMappingEditor({ ...p, mapping: p.mapping || {}, headers: [] });
                  }}
                  data-testid="bp-bank-preset-edit-mapping">
                   Edit columns
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => deleteBankPreset(activePresetId)} data-testid="bp-bank-preset-delete">
                   Delete
                </button>
              </>
            )}
            <input
              className="input" style={{ width: 140 }}
              placeholder="New bank name…"
              value={presetPickerName}
              onChange={(e) => setPresetPickerName(e.target.value)}
              data-testid="bp-bank-preset-name" />
            <button
              className="btn btn-ghost btn-sm"
              onClick={saveBankPreset}
              disabled={!presetPickerName.trim()}
              data-testid="bp-bank-preset-save">
               Save as bank preset
            </button>
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => document.getElementById("bp-csv-input")?.click()}
          data-testid="bp-csv-drop"
          style={{
            border: `1.5px dashed ${dragOver ? "#D4AF37" : "rgba(255,255,255,0.18)"}`,
            background: dragOver ? "rgba(212,175,55,0.06)" : "rgba(255,255,255,0.02)",
            borderRadius: 10, padding: "var(--space-4)", textAlign: "center", cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <div className="fs-14 fw-700 mb-4"> Drag & drop bank export CSV</div>
          <div className="text-muted fs-12">
            or click to browse · UTRs auto-match to pending payouts by amount and booking reference
          </div>
          <input
            id="bp-csv-input"
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => uploadCsv(e.target.files?.[0])}
            data-testid="bp-csv-input"
          />
        </div>

        {csvBusy && <div className="text-muted fs-12 mt-8">Working on it…</div>}

        {csvPreview && (
          <div className="card card-pad mt-8" data-testid="bp-csv-preview"
                style={{ background: "rgba(255,255,255,0.02)" }}>
            <div className="flex-between mb-8">
              <div>
                <b>{csvPreview.matched.length}</b> matched ·
                <b className="text-warn">{" "}{csvPreview.ambiguous.length}</b> ambiguous ·
                <b style={{ color: "#e57373" }}>{" "}{csvPreview.unmatched.length}</b> unmatched
                {" · "}
                <span className="text-muted fs-12">
                  {csvPreview.candidates_missing_in_csv} pending payout(s) not covered
                </span>
              </div>
              <div className="flex gap-8">
                <button className="btn btn-ghost btn-sm" onClick={() => setCsvPreview(null)}>Cancel</button>
                <button className="btn btn-gold btn-sm"
                        onClick={applyCsvMatched}
                        disabled={csvBusy || !csvPreview.matched.length}
                        data-testid="bp-csv-apply">
                   Apply {csvPreview.matched.length} matched
                </button>
              </div>
            </div>
            {csvPreview.matched.length > 0 && (
              <table className="tbl">
                <thead><tr><th>Booking</th><th>Match</th><th>Amount</th><th>Outstanding</th><th>UTR</th></tr></thead>
                <tbody>
                  {csvPreview.matched.slice(0, 10).map((m) => (
                    <tr key={m.booking_id + m.row} data-testid={`bp-csv-m-${m.booking_id}`}>
                      <td>{m.booking_ref}</td>
                      <td><span className="pill pill-green">{m.match_reason}</span></td>
                      <td>{fmtMoneyNumber(m.amount)}</td>
                      <td>{fmtMoneyNumber(m.outstanding)}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{m.utr || "—"}</td>
                    </tr>
                  ))}
                  {csvPreview.matched.length > 10 && (
                    <tr><td colSpan={5} className="text-muted fs-11">…and {csvPreview.matched.length - 10} more matched rows</td></tr>
                  )}
                </tbody>
              </table>
            )}
            {csvPreview.ambiguous.length > 0 && (
              <div className="text-muted fs-11 mt-4">
                 {csvPreview.ambiguous.length} row(s) match multiple candidates — resolve manually below.
              </div>
            )}
            {csvPreview.unmatched.length > 0 && (
              <div className="text-muted fs-11 mt-4">
                 {csvPreview.unmatched.length} row(s) had no match — likely non-payout entries or missing bookings.
              </div>
            )}
          </div>
        )}
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>Booking</th><th>Event Date</th><th>Payout Due Date</th><th>Artist</th>
            <th>Outstanding</th><th>Amount</th><th>UTR / Ref</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} className="empty">No pending payouts — everyone's been paid</td></tr>}
          {rows.map((r) => (
            <tr key={r.booking_id} data-testid={`bp-row-${r.booking_id}`}>
              <td><input type="checkbox" checked={!!selected[r.booking_id]} onChange={() => toggle(r.booking_id)} data-testid={`bp-check-${r.booking_id}`} /></td>
              <td><Link to={`/bookings/${r.booking_id}`}>{r.booking_ref}</Link></td>
              <td>{r.event_date || "—"}</td>
              <td>{r.payout_due_date || r.event_date || "—"}</td>
              <td title={r.artist_email}>{r.artist_name || r.artist_email}</td>
              <td>{fmtMoneyNumber(r.outstanding)}</td>
              <td>
                <input
                  type="number"
                  min="0"
                  className="input"
                  style={{ width: 110 }}
                  placeholder={String(r.outstanding)}
                  value={r.pay_amount ?? ""}
                  onChange={(e) => updateRow(r.booking_id, { pay_amount: e.target.value })}
                  data-testid={`bp-amt-${r.booking_id}`}
                />
              </td>
              <td>
                <input
                  className="input"
                  style={{ width: 160 }}
                  placeholder="UTR / txn id"
                  value={r.pay_utr ?? ""}
                  onChange={(e) => updateRow(r.booking_id, { pay_utr: e.target.value })}
                  data-testid={`bp-utr-${r.booking_id}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {mappingEditor && (
        <BankPresetMapperModal
          preset={mappingEditor}
          existingHeaders={csvPreview ? Object.keys(
            (csvPreview.matched[0]?.raw)
            || (csvPreview.unmatched[0]?.raw)
            || (csvPreview.ambiguous[0]?.raw)
            || {}
          ) : []}
          onCancel={() => setMappingEditor(null)}
          onSave={async (nextMapping) => {
            try {
              await api.patch(`/admin/payouts/bank-presets/${mappingEditor.id}`, {
                bank_name: mappingEditor.bank_name,
                mapping: nextMapping,
              });
              toast(`Updated columns for ${mappingEditor.bank_name}`, "success");
              setMappingEditor(null);
              loadBankPresets();
            } catch (e) { toast(formatApiError(e), "error"); }
          }}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// BankPresetMapperModal — visual CSV-header  BookTalent-field mapper.
// ────────────────────────────────────────────────────────────────────────
function BankPresetMapperModal({ preset, existingHeaders = [], onCancel, onSave }) {
  const FIELDS = [
    { key: "amount",   label: "Amount",       hint: "e.g. Debit, Credit, Amount" },
    { key: "utr",      label: "UTR / Ref no", hint: "e.g. UTR, Reference No, Txn ID" },
    { key: "ref_hint", label: "Reference hint", hint: "Free-text field that contains the booking ref — e.g. Narration, Description" },
    { key: "paid_on",  label: "Paid on",      hint: "Date column — e.g. Value Date, Txn Date" },
  ];
  const [mapping, setMapping] = useState(() => {
    const m = {};
    FIELDS.forEach((f) => { m[f.key] = (preset.mapping || {})[f.key] || []; });
    return m;
  });
  const [headers, setHeaders] = useState(existingHeaders);

  const parseHeaders = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const line = String(e.target.result || "").split(/\r?\n/)[0] || "";
      setHeaders(line.split(",").map((s) => s.trim().replace(/^["']|["']$/g, "")).filter(Boolean));
    };
    reader.readAsText(file);
  };

  const toggle = (field, header) => {
    setMapping((m) => {
      const cur = new Set(m[field]);
      if (cur.has(header)) cur.delete(header);
      else cur.add(header);
      return { ...m, [field]: Array.from(cur) };
    });
  };

  return (
    <div
      data-testid="bp-mapper-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(36,34,31,0.36)",
        display: "grid", placeItems: "center", zIndex: 900, padding: "var(--space-4)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onCancel}>
      <div className="card card-pad" style={{ maxWidth: 720, width: "100%", background: "var(--bg1)", color: "var(--white)", maxHeight: "88vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif fw-700 fs-18 mb-4"> Column mapping — {preset.bank_name}</h3>
        <p className="text-muted fs-12 mb-12">
          Tick which CSV headers hold each BookTalent field. Multi-select is fine — the parser tries them in order.
        </p>

        <div className="field-label mb-4">CSV headers</div>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
          {headers.length === 0 && <span className="text-muted fs-11">No headers loaded yet — pick a sample CSV to detect them</span>}
          {headers.map((h) => (
            <span key={h} style={{
              fontSize: 11, padding: "var(--space-1) 10px", borderRadius: 999,
              background: "var(--bg3)", border: "1px solid var(--glass-border)",
            }}>{h}</span>
          ))}
          <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
             Load sample
            <input type="file" accept=".csv" style={{ display: "none" }}
              onChange={(e) => e.target.files?.[0] && parseHeaders(e.target.files[0])}
              data-testid="bp-mapper-sample" />
          </label>
        </div>

        {FIELDS.map((f) => (
          <div key={f.key} className="mb-12" data-testid={`bp-mapper-field-${f.key}`}>
            <div className="fw-700 fs-13">{f.label}</div>
            <div className="text-muted fs-11 mb-4">{f.hint}</div>
            <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
              {headers.length === 0 && (mapping[f.key] || []).map((h) => (
                <span key={h} className="pill pill-gold" style={{ fontSize: 11 }}>{h} ✓</span>
              ))}
              {headers.map((h) => {
                const on = (mapping[f.key] || []).includes(h);
                return (
                  <span key={h}
                    onClick={() => toggle(f.key, h)}
                    style={{
                      cursor: "pointer",
                      fontSize: 11, padding: "var(--space-1) 10px", borderRadius: 999,
                      border: `1px solid ${on ? "var(--gold-border)" : "var(--glass-border)"}`,
                      background: on ? "var(--gold-dim)" : "var(--bg3)",
                      fontWeight: on ? 700 : 400,
                    }}
                    data-testid={`bp-mapper-${f.key}-${h}`}>
                    {on ? "✓ " : ""}{h}
                  </span>
                );
              })}
            </div>
          </div>
        ))}

        <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} data-testid="bp-mapper-cancel">Cancel</button>
          <button className="btn btn-gold btn-sm" onClick={() => onSave(mapping)} data-testid="bp-mapper-save">Save mapping</button>
        </div>
      </div>
    </div>
  );
}



// ────────────────────────────────────────────────────────────────────────
// Admin Commercial Deals page — every artist's Normal/Service + % status
// with inline edit and per-artist deal-change history drawer.
// ────────────────────────────────────────────────────────────────────────
function AdminCommercialDeals({ toast }) {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [total, setTotal] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [editing, setEditing] = useState(null);      // artist_id being edited
  const [draft, setDraft] = useState({ artist_type: "normal", percentage_deal: 0 });
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState(null); // {artist_id, name}
  const [historyRows, setHistoryRows] = useState([]);
  const tableScrollRef = useRef(null);

  const load = async (targetPage = page) => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (appliedQ) params.set("q", appliedQ);
      if (typeFilter) params.set("type_filter", typeFilter);
      params.set("page", String(targetPage));
      params.set("limit", String(limit));
      const r = await api.get(`/admin/artists/commercial-deals?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotals(r.data?.totals || {});
      setTotal(Number(r.data?.total) || 0);
    } catch (e) {
      toast(formatApiError(e), "error");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    setScrollTop(0);
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, appliedQ, page, limit]);

  const applySearch = () => {
    setAppliedQ(q.trim());
    setPage(1);
  };

  const pageCount = Math.max(1, Math.ceil(total / limit));
  const rowHeight = 72;
  const viewportHeight = 504;
  const overscan = 5;
  const firstVisible = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const lastVisible = Math.min(
    items.length,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan,
  );
  const visibleItems = items.slice(firstVisible, lastVisible);

  const openHistory = async (artist) => {
    setHistoryFor(artist);
    try {
      const r = await api.get(`/admin/artists/${artist.artist_id}/deal-history`);
      setHistoryRows(r.data?.items || []);
    } catch (e) { toast(formatApiError(e), "error"); setHistoryRows([]); }
  };

  const startEdit = (row) => {
    setEditing(row.artist_id);
    setDraft({
      artist_type: row.artist_type || (row.is_service_artist ? "service" : "normal"),
      percentage_deal: Number(row.percentage_deal) || 0,
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft({ artist_type: "normal", percentage_deal: 0 }); };

  const saveDeal = async (row) => {
    // Validate before hitting API
    if (draft.artist_type === "service" && (!draft.percentage_deal || draft.percentage_deal <= 0)) {
      toast("Service artists ke liye Commission % 0 se zyada hona chahiye", "error");
      return;
    }
    setSaving(true);
    try {
      const body = {
        artist_type: draft.artist_type,
        percentage_deal: draft.artist_type === "service" ? Number(draft.percentage_deal) : 0,
      };
      await api.patch(`/admin/artists/${row.artist_id}/commercial-deal`, body);
      toast(`Updated deal for ${row.name}`, "success");
      cancelEdit();
      load(page);
    } catch (e) { toast(formatApiError(e), "error"); }
    setSaving(false);
  };

  return (
    <div className="card" data-testid="admin-commercial-deals">
      <div className="card-head">
        <div>
          <div className="card-title">Commercial Deals</div>
          <div className="text-muted fs-12">
            Review and update commercial terms for live artists.
          </div>
        </div>
        <span className="pill pill-blue" data-testid="cd-total">{total.toLocaleString("en-IN")} artists</span>
      </div>

      <div className="kpi-grid" style={{ padding: "var(--space-2) 14px" }}>
        <Kpi icon="" cls="kpi-icon-blue" num={totals.count || 0} label="Total Artists" />
        <Kpi icon="" cls="kpi-icon-gold" num={totals.service_count || 0} label="Service Artists" />
        <Kpi icon="" cls="kpi-icon-violet" num={totals.normal_count || 0} label="Normal Artists" />
        <Kpi icon="" cls="kpi-icon-green" num={`${totals.avg_service_pct || 0}%`} label="Avg. Service %" />
      </div>

      <div className="cd-filters">
        <input
          className="input"
          placeholder="Search artist, email, or city"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySearch()}
          data-testid="cd-q"
        />
        <select
          className="input"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          data-testid="cd-type"
        >
          <option value="">All types</option>
          <option value="service">BookTalent Service only</option>
          <option value="normal">Normal only</option>
        </select>
        <label className="cd-page-size">
          <span>Rows per page</span>
          <select
            className="input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            data-testid="cd-page-size"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </label>
        <button className="btn btn-gold btn-sm" onClick={applySearch} disabled={busy} data-testid="cd-apply">
          {busy ? "Loading…" : "Apply filters"}
        </button>
      </div>

      <div className="cd-table-scroll" ref={tableScrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} aria-busy={busy}>
        <table className="table cd-table">
          <thead>
            <tr>
              <th>Artist</th><th>City</th><th>Type</th><th>Commission %</th>
              <th>KYC</th><th>Set On</th><th>By</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  {busy ? "Loading commercial deals…" : "No artists match these filters."}
                </td>
              </tr>
            )}
            {firstVisible > 0 && (
              <tr className="cd-virtual-spacer" aria-hidden="true">
                <td colSpan={8} style={{ height: firstVisible * rowHeight }} />
              </tr>
            )}
            {visibleItems.map((row) => {
            const isEditing = editing === row.artist_id;
            return (
              <tr key={row.artist_id} data-testid={`cd-row-${row.artist_id}`}>
                <td>
                  <div className="fw-700 fs-13">{row.name}</div>
                  <div className="text-muted fs-11">{row.email}</div>
                </td>
                <td>{row.city || "—"}</td>
                <td>
                  {isEditing ? (
                    <select
                      value={draft.artist_type}
                      onChange={(e) => setDraft({ ...draft, artist_type: e.target.value, percentage_deal: e.target.value === "normal" ? 0 : draft.percentage_deal })}
                      data-testid={`cd-type-${row.artist_id}`}
                      className="input" style={{ padding: "var(--space-1) 6px" }}>
                      <option value="normal">Normal</option>
                      <option value="service">Service</option>
                    </select>
                  ) : (
                    <span className={`pill ${row.is_service_artist ? "pill-green" : "pill-violet"}`}>
                      {row.is_service_artist ? "Service" : "Normal"}
                    </span>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    draft.artist_type === "service" ? (
                      <input
                        type="number" min="0.5" max="50" step="0.5"
                        value={draft.percentage_deal}
                        onChange={(e) => setDraft({ ...draft, percentage_deal: e.target.value })}
                        data-testid={`cd-pct-${row.artist_id}`}
                        className="input" style={{ width: 80, padding: "var(--space-1) 6px" }}
                        placeholder="e.g. 10"
                      />
                    ) : (
                      <span className="text-muted fs-11">N/A (Normal)</span>
                    )
                  ) : (
                    row.is_service_artist ? <b>{row.percentage_deal}%</b> : "—"
                  )}
                </td>
                <td><span className="pill pill-blue">{row.kyc_status}</span></td>
                <td className="fs-11">{(row.commercial_deal_set_at || "").slice(0, 10) || "—"}</td>
                <td className="fs-11 text-muted">{row.commercial_deal_set_by || "—"}</td>
                <td>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: "var(--space-1)" }}>
                      <button className="btn btn-gold btn-sm" onClick={() => saveDeal(row)} disabled={saving} data-testid={`cd-save-${row.artist_id}`}>
                        {saving ? "Saving…" : " Save"}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving} data-testid={`cd-cancel-${row.artist_id}`}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "var(--space-1)" }}>
                      <button className="btn btn-gold btn-sm" onClick={() => startEdit(row)} data-testid={`cd-edit-${row.artist_id}`}>✎ Edit</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openHistory(row)} data-testid={`cd-hist-${row.artist_id}`}> History</button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
            {lastVisible < items.length && (
              <tr className="cd-virtual-spacer" aria-hidden="true">
                <td colSpan={8} style={{ height: (items.length - lastVisible) * rowHeight }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="cd-pagination-row">
        <span className="text-muted fs-12" data-testid="cd-page-summary">
          {total === 0 ? "No artists" : `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total.toLocaleString("en-IN")}`}
        </span>
        {pageCount > 1 && (
          <Pagination className="cd-pagination">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page <= 1}
                  onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }}
                  data-testid="cd-prev"
                />
              </PaginationItem>
              <PaginationItem>
                <span className="cd-page-indicator" aria-current="page">Page {page} of {pageCount}</span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page >= pageCount}
                  onClick={(e) => { e.preventDefault(); if (page < pageCount) setPage(page + 1); }}
                  data-testid="cd-next"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {historyFor && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(36,34,31,0.36)",
          display: "grid", placeItems: "center", zIndex: 900, padding: "var(--space-4)",
        }} onClick={() => setHistoryFor(null)} data-testid="cd-history-modal">
          <div className="card card-pad" style={{ maxWidth: 640, width: "100%", maxHeight: "80vh", overflow: "auto", background: "var(--bg1)", color: "var(--white)" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif fw-700 fs-18 mb-4"> Deal history — {historyFor.name}</h3>
            <p className="text-muted fs-12 mb-12">Every change to this artist's commercial deal.</p>
            {historyRows.length === 0 ? (
              <div className="text-muted fs-13">No historical changes recorded — deal was set once during KYC approval.</div>
            ) : (
              <table className="tbl">
                <thead><tr><th>When</th><th>Action</th><th>Type</th><th>%</th><th>By</th></tr></thead>
                <tbody>
                  {historyRows.map((h) => (
                    <tr key={h.id} data-testid={`cd-hist-row-${h.id}`}>
                      <td>{(h.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="fs-11 text-muted">{h.action}</td>
                      <td>{h.metadata?.artist_type || "—"}</td>
                      <td>{h.metadata?.percentage_deal ?? "—"}</td>
                      <td className="fs-11">{h.actor_email || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ textAlign: "right", marginTop: "var(--space-3)" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setHistoryFor(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
